/* eslint-disable react/prop-types */
/**
 * RestroPulse POS · posReducer (Sprint 2 · multi-order state)
 *
 * DOMAIN VOCABULARY — not UI vocabulary. This reducer will become the
 * seed of the backend event model, so every action reads as a POS
 * business event.
 *
 * STATE SHAPE
 * -----------
 * The store now holds MANY concurrent orders. A floor of 20 tables can
 * have 20 different orders in flight simultaneously. Only one order is
 * "active" at any time — the one the billing screen is currently
 * showing. The floor view is what you see when NO order is active.
 *
 *     state.orders          → { [order_id]: { order, lines, payments } }
 *     state.active_order_id → id of the order the billing screen shows,
 *                             or null when the terminal is on the floor
 *     state.session         → outlet-scoped counters (KOT, invoice seq),
 *                             cancellation audit, dirty-table list
 *
 * DERIVED TABLE STATE — Contract §5. Never stored on the table object:
 *     VACANT   no active order for the table, not dirty
 *     OCCUPIED an active order in OPEN or HELD
 *     BILLED   an active order in PRINTED
 *     DIRTY    no active order, dirty flag set
 *
 * KOT numbering and invoice numbering live on `session` and march
 * monotonically across every order in the outlet's business day.
 * Money is computed on every dispatch — never a stored derived field.
 */
import { ITEMS, TAX_CONFIG, TABLES, tax_rounding_mode, TAX_ROUNDING_MODES, OUTLET_CONFIG, STAFF, IDLE_LOCK_SECONDS } from "./mockData";
// Sprint 3B correction · exported so BillingScreen surfaces (KOT
// confirm dialog, ctx-table pill, transfer dialogs) resolve `T1`
// instead of leaking `t_g01`. Same fault class as the consolidation
// sprint — this time on the dialog the same sprint had just added.
export const resolveTableLabel = (table_id) => {
  if (!table_id) return null;
  const t = TABLES.find((x) => x.id === table_id);
  return t?.label || table_id;
};

// Sprint 7 correction 3 · lookup helpers for staff login. `getActor`
// returns the currently-logged-in staff or a neutral placeholder if
// nobody is logged in — every reducer that stamps an actor onto an
// action reads through this so no hardcoded mock id survives.
export const findStaffByPin = (pin) =>
  STAFF.find((s) => s.active && s.pin === (pin || "").trim());
export const findStaffById = (id) =>
  STAFF.find((s) => s.id === id);
export const getActor = (state) => {
  const id = state?.session?.logged_in_staff_id;
  const snap = state?.session?.staff_snapshot;
  const s  = (snap && snap.id === id) ? snap : (id ? findStaffById(id) : null);
  return s || { id: "system", name: "system", role: "system" };
};

// ─── Role permission matrix ─────────────────────────────────────────
// Mirrors backend `PERMISSIONS` in /app/backend/routes/pos_auth.py.
// Client copy is used ONLY to gate the UI (hide/disable buttons); the
// server re-checks every mutating and every settlement request against
// the presented token, so a role change (e.g. steward unlocking after
// a cashier) never opens up a UI-hidden action via a raw API call.
export const POS_PERMISSIONS = {
  steward:      { open_table: true, print_bill: true, settle: false, shift_open_close: false,
                  discount_within_ceiling: false, authorise: false, reopen_bill: false,
                  close_business_day: false, manage_staff: false,
                  change_pos_settings: false, change_tax_config: false },
  bartender:    { open_table: true, print_bill: true, settle: false, shift_open_close: false,
                  discount_within_ceiling: false, authorise: false, reopen_bill: false,
                  close_business_day: false, manage_staff: false,
                  change_pos_settings: false, change_tax_config: false },
  cashier:      { open_table: true, print_bill: true, settle: true,  shift_open_close: true,
                  discount_within_ceiling: true,  authorise: false, reopen_bill: false,
                  close_business_day: false, manage_staff: false,
                  change_pos_settings: false, change_tax_config: false },
  supervisor:   { open_table: true, print_bill: true, settle: true,  shift_open_close: true,
                  discount_within_ceiling: true,  authorise: true,  reopen_bill: false,
                  close_business_day: false, manage_staff: false,
                  change_pos_settings: false, change_tax_config: false },
  manager:      { open_table: true, print_bill: true, settle: true,  shift_open_close: true,
                  discount_within_ceiling: true,  authorise: true,  reopen_bill: true,
                  close_business_day: true,  manage_staff: true,
                  change_pos_settings: false, change_tax_config: false },
  outlet_admin: { open_table: true, print_bill: true, settle: true,  shift_open_close: true,
                  discount_within_ceiling: true,  authorise: true,  reopen_bill: true,
                  close_business_day: true,  manage_staff: true,
                  change_pos_settings: true,  change_tax_config: false },
};

/** True if the currently-logged-in staff can perform `action`. */
export const actorCan = (state, action) => {
  const role = getActor(state).role;
  return !!POS_PERMISSIONS[role]?.[action];
};

/**
 * Sprint-1 backend integration · session gate.
 *
 * A logged-in cashier or manager with NO shift open must not be able to
 * open a table, punch a line, fire a ticket or run any billing action.
 * A steward logging in with no shift open must be told to wait — a
 * steward cannot open a shift.
 *
 * The reducer becomes the truth for this sprint. When orders start
 * persisting server-side in Sprint 2, the same rule enforces there
 * (via `require_shift_open` on the POS session dep) without needing to
 * restructure the client — the client already refuses at source.
 *
 * `sessionGuard(state)` returns:
 *   {
 *     ok:      boolean,        // any billing action is safe
 *     reason:  'no-staff' | 'no-shift' | 'steward-wait' | null,
 *     message: human-readable prompt (or null)
 *   }
 * UI reads this to render the wait-for-cashier / open-shift prompt.
 * Reducer reads this to refuse every billing case with the same rule.
 */
export function sessionGuard(state) {
  const sess = state?.session || {};
  const staff = sess.staff_snapshot
    || (sess.logged_in_staff_id ? findStaffById(sess.logged_in_staff_id) : null);
  if (!staff || sess.locked) {
    return { ok: false, reason: "no-staff",
             message: "Sign in to continue." };
  }
  const openShift = (sess.shifts || []).find((sh) => !sh.closed_at);
  if (openShift) return { ok: true, reason: null, message: null };

  // No shift open — actions blocked. Steward gets a different prompt
  // because they cannot open one themselves.
  if (staff.role === "steward") {
    return { ok: false, reason: "steward-wait",
             message: "No shift is open yet. Please wait for a cashier or manager to open the shift." };
  }
  return { ok: false, reason: "no-shift",
           message: "No shift is open. Open a shift before taking orders." };
}

const BILLING_ACTIONS = new Set([
  "TABLE_OPENED",   "ORDER_OPENED",   "ORDER_ACTIVATED",
  "LINE_ADDED",     "LINE_ADDED_OPEN","LINE_QTY_CHANGED",
  "LINE_NOTE_SET",  "LINE_DISCOUNT_SET","LINE_VOIDED",
  "TICKET_SENT",    "BILL_PRINTED",   "BILL_REPRINTED",
  "PAYMENT_TENDERED","PAYMENT_STATUS_UPDATED","PAYMENT_REVERSED",
  "ORDER_SETTLED",  "ORDER_VOIDED",   "ORDER_HELD", "ORDER_RESUMED",
  "DISCOUNT_APPLIED","OPEN_ITEM_CREATED","AGGREGATOR_ACCEPTED",
]);
// Item 2 (Sprint 4 corrections) · resolve internal table_id → display
// label everywhere it lands on a user-facing or print surface. The
// kitchen cannot match "t_g03" to a physical table; it must read "T3".
const tableLabelFor = (table_id) => {
  if (!table_id) return null;
  const t = TABLES.find((x) => x.id === table_id);
  return t?.label || table_id;
};

/** Routing helper — returns "kitchen" or "bar" from item id. */
export const routingForItem = (item_id) => {
  const it = ITEMS.find((x) => x.id === item_id);
  return it?.routing || "kitchen";
};

export const ORDER_STATE = Object.freeze({
  OPEN:    "OPEN",     // in-progress; may have unfired lines OR all lines fired
  PRINTED: "PRINTED",  // bill printed, tender in progress, editing locked
  SETTLED: "SETTLED",  // payment complete, order closed / read-only
  VOIDED:  "VOIDED",   // terminal; order abandoned before settle
  HELD:    "HELD",     // side state reachable only from OPEN
});

export const ORDER_TYPE = Object.freeze({
  DINE_IN:      "DINE_IN",
  TAKEAWAY:     "TAKEAWAY",
  DELIVERY:     "DELIVERY",
  ROOM_SERVICE: "ROOM_SERVICE",
});

// ─── Actions (domain events, never UI verbs) ────────────────────────
// ⚠ CONTRACT NOTE (Part 0): Only THREE contract events actually cross
// the wire in the real system: ORDER_OPENED, TABLE_ASSIGNED, COVERS_SET
// (see Contract §4 event vocabulary). Every other ACT.* here is either
// a UI-local action (NAVIGATE_TO_FLOOR, ORDER_ACTIVATED, TERMINAL_CLEARED,
// CONNECTION_TOGGLED) or a COMPOUND UI action (TABLE_OPENED) that expands
// into those three wire events atomically inside the reducer. This
// separation keeps the "one dispatch = one user intent" ergonomic while
// guaranteeing the wire payload is minimal, ordered, and idempotent.
export const ACT = Object.freeze({
  // Order lifecycle
  ORDER_OPENED:      "ORDER_OPENED",       // creates a fresh non-dine-in order and activates it
  TABLE_OPENED:      "TABLE_OPENED",       // creates a dine-in order attached to a table; emits ORDER_OPENED + TABLE_ASSIGNED + COVERS_SET internally
  AGGREGATOR_ACCEPTED:   "AGGREGATOR_ACCEPTED",   // Part 6 · imports aggregator payload as a new order + fires tickets
  AGGREGATOR_REJECTED:   "AGGREGATOR_REJECTED",   // Part 6 · records reason and drops from tray

  GUEST_DETAILS_UPDATED: "GUEST_DETAILS_UPDATED", // Part 5 · attach phone/name/address/consent to any non-terminal order
  TABLE_ASSIGNED:    "TABLE_ASSIGNED",     // attach table_id to active order
  COVERS_SET:        "COVERS_SET",         // set covers on active order
  ORDER_ACTIVATED:   "ORDER_ACTIVATED",    // navigate to billing for an existing order
  NAVIGATE_TO_FLOOR: "NAVIGATE_TO_FLOOR",  // leave billing, back to floor view (no state change)
  // Sprint 6 correction 5 · replace the entire state with a fresh
  // demo snapshot. Used ONLY by the public /pos-demo entry point.
  DEMO_RESET:        "DEMO_RESET",
  // Sprint 7 correction 3 · Staff login lifecycle.
  STAFF_LOGGED_IN:   "STAFF_LOGGED_IN",
  STAFF_LOCKED:      "STAFF_LOCKED",
  STAFF_LOGGED_OUT:  "STAFF_LOGGED_OUT",
  ORDER_TYPE_SET:    "ORDER_TYPE_SET",     // switch the active order's type (dine-in/takeaway/…)
  // Lines
  LINE_ADDED:        "LINE_ADDED",
  LINE_ADDED_OPEN:   "LINE_ADDED_OPEN",     // Part 8 · open-item punch
  LINE_QTY_CHANGED:  "LINE_QTY_CHANGED",
  LINE_NOTE_SET:     "LINE_NOTE_SET",
  LINE_DISCOUNT_SET: "LINE_DISCOUNT_SET",
  LINE_VOIDED:       "LINE_VOIDED",
  // Kitchen · Part 7 · destination-scoped ticket firing.
  TICKET_SENT:       "TICKET_SENT",
  TICKET_REPRINTED:  "TICKET_REPRINTED",
  TICKET_ITEM_CANCELLED: "TICKET_ITEM_CANCELLED",
  // Bill & payment
  BILL_PRINTED:      "BILL_PRINTED",
  PAYMENT_TENDERED:  "PAYMENT_TENDERED",
  PAYMENT_REVERSED:  "PAYMENT_REVERSED",
  PAYMENT_STATUS_UPDATED: "PAYMENT_STATUS_UPDATED", // Part 4.4 · pay_online pending → confirmed
  // Sprint 5 correction · lets Enter chain through settlement:
  // tender fires immediately (balance flips), then the reference is
  // attached to that same tender via this action when the cashier
  // types it in and hits Enter again.
  PAYMENT_REFERENCE_UPDATED: "PAYMENT_REFERENCE_UPDATED",
  ORDER_SETTLED:     "ORDER_SETTLED",
  ORDER_VOIDED:      "ORDER_VOIDED",
  // Hold / resume
  ORDER_HELD:        "ORDER_HELD",
  ORDER_RESUMED:     "ORDER_RESUMED",
  // Table lifecycle
  TABLE_MARKED_DIRTY:"TABLE_MARKED_DIRTY", // Part 3 · retained as internal alias for grace; UI no longer shows DIRTY
  TABLE_CLEARED:     "TABLE_CLEARED",      // dirty → vacant (Mark clean)
  TABLE_MARKED_UNAVAILABLE: "TABLE_MARKED_UNAVAILABLE", // Part 3 · free-text reason
  TABLE_MADE_AVAILABLE: "TABLE_MADE_AVAILABLE", // clears the unavailable flag and returns the tile to VACANT (via tableState derivation)
  TABLE_TRANSFERRED: "TABLE_TRANSFERRED",  // Part 7 · move whole order to another table
  ITEM_TRANSFERRED:  "ITEM_TRANSFERRED",   // Part 7 · move a line to another order
  // Money · Part 5 (discount) and Part 8 (reprint)
  DISCOUNT_APPLIED:  "DISCOUNT_APPLIED",   // Part 5 · order-level discount, percent or absolute
  BILL_REPRINTED:    "BILL_REPRINTED",     // Part 8 · same invoice number, reprint counter++
  // Part 6 · split bill
  ORDER_SPLIT:       "ORDER_SPLIT",        // creates one or more children from parent
  INVOICE_VOIDED:    "INVOICE_VOIDED",     // Contract §4 · voids one printed invoice (typically prior to post-print split) without terminating the underlying order. Distinct from ORDER_VOIDED.
  // Part 9 · Temporary tables
  TEMP_TABLE_CREATED: "TEMP_TABLE_CREATED",
  TEMP_TABLE_REMOVED: "TEMP_TABLE_REMOVED",
  // Part 8 · Open item permission toggle
  OPEN_ITEM_TOGGLED:  "OPEN_ITEM_TOGGLED",
  // Sprint 4 correction · Managed open-item list (business-day scoped).
  // Manager PIN required to create; entries persist until the day
  // closes and reappear as a punchable "Open items" category in the
  // menu area.
  OPEN_ITEM_CREATED:  "OPEN_ITEM_CREATED",
  // Terminal
  TERMINAL_CLEARED:  "TERMINAL_CLEARED",   // Sprint-2 post-settle: closes the active-order slot
  ORDER_DRAFT_RELEASED: "ORDER_DRAFT_RELEASED", // POS Cleanup Pass 1 · server-persisted draft discard
  CONNECTION_TOGGLED:"CONNECTION_TOGGLED", // demo-only for offline banner
  // Sprint 3 · business day + shift lifecycle
  BUSINESS_DAY_OPENED:  "BUSINESS_DAY_OPENED",    // Part 6 · sets session.business_date; blocks orders until fired
  BUSINESS_DAY_CLOSED:  "BUSINESS_DAY_CLOSED",    // Part 6 · closes the day (guarded by outer reducer)
  SHIFT_OPENED:         "SHIFT_OPENED",           // Part 7 · records opening float + opener
  SHIFT_CLOSED:         "SHIFT_CLOSED",           // Part 7 · records counted + variance + reason
  X_REPORT_TAKEN:       "X_REPORT_TAKEN",         // Part 8 · non-destructive read, repeatable
  Z_REPORT_GENERATED:   "Z_REPORT_GENERATED",     // Part 8 · fiscal artifact, sequentially numbered
  // Sprint 3 · KDS + token queue
  TICKET_MARKED_READY:  "TICKET_MARKED_READY",    // Part 11 · KDS mark ready
  TOKEN_MARKED_COLLECTED: "TOKEN_MARKED_COLLECTED", // Part 12 · QSR token collected
  // Sprint 5 correction · manual promotion from Preparing → Ready.
  // Both routes must work: KDS bump (which the TICKET_MARKED_READY
  // handler already covers) AND direct tap on a Preparing token.
  TOKEN_MARKED_READY:     "TOKEN_MARKED_READY",
  SLIP_ACKNOWLEDGED:    "SLIP_ACKNOWLEDGED",     // Part 10 · dismisses the top slip from the queue
  // ── Sprint 2 Checkpoint B.2 · Live persistence (posLiveActions) ────
  // These actions are dispatched EXCLUSIVELY by posLiveActions.js;
  // components never dispatch them directly. They wire the reducer to
  // the /api/pos/* system of record while preserving optimistic UX.
  ORDER_PENDING_SET:     "ORDER_PENDING_SET",
  ORDER_PENDING_CLEAR:   "ORDER_PENDING_CLEAR",
  LINE_PENDING_SET:      "LINE_PENDING_SET",
  LINE_PENDING_CLEAR:    "LINE_PENDING_CLEAR",
  ORDER_SYNCED_FROM_SERVER: "ORDER_SYNCED_FROM_SERVER",
  SLICE_ROLLBACK:        "SLICE_ROLLBACK",
  ORDER_ERROR_SET:       "ORDER_ERROR_SET",
  ORDER_ERROR_CLEAR:     "ORDER_ERROR_CLEAR",
  LINE_ID_REBOUND:       "LINE_ID_REBOUND",
  LIVE_ORDER_HYDRATED:   "LIVE_ORDER_HYDRATED",   // rebuild an order slice from the server payload on mount / reload
  SESSION_HYDRATED:      "SESSION_HYDRATED",      // rebuild business_day + shift on mount / reload
  SESSION_IDLE_LOCK_SET: "SESSION_IDLE_LOCK_SET", // tenant-configured idle-lock seconds from /session-status
  SESSION_ROUNDING_SET:  "SESSION_ROUNDING_SET",  // outlet-configured tax rounding mode from /session-status
  // ── Sprint 2 B.2 · Till lifecycle (per-staff cash, gates settlement) ──
  // A Till is strictly per-staff. It carries the opening float and the
  // declared cash at close. Settlement is refused when no till is
  // open. Manager can decline handling cash on login — no till opens
  // and settlement stays refused for that manager's session.
  TILL_OPENED:           "TILL_OPENED",
  TILL_CLOSED:           "TILL_CLOSED",
  TILL_HYDRATED:         "TILL_HYDRATED",
  TILL_PROMPT_DISMISSED: "TILL_PROMPT_DISMISSED",   // steward/bartender never see prompt; also used when cashier defers
  TILL_WARN_HOURS_SET:   "TILL_WARN_HOURS_SET",     // tenant-scoped duration nudge (Sprint 2 B.2 correction 5)
});

// ─── Initial state ──────────────────────────────────────────────────
export const initialState = {
  session: {
    outlet_code:       "WE",       // Woodland Eatery, mock
    fy:                "25-26",    // financial year, mock
    // Part 7 · per-destination ticket sequences (kitchen and bar).
    ticket_seq:        { kitchen: 0, bar: 0 },
    // NOTE: `invoice_seq` here is a PROTOTYPE-ONLY mock allocator.
    // Per Contract §7, production invoice numbers are allocated by the
    // STATION from cloud-leased blocks, never by a terminal — two
    // terminals allocating independently would collide. When the
    // backend lands, this counter is removed and the terminal reads
    // numbers from station-issued blocks over the local network.
    invoice_seq:       0,
    ticket_cancellations: [],
    // Part 3 — dirty flag replaced by 120s grace period. Table shows a
    // muted "cleaning" dot for 120s after settlement, then auto-clears
    // to VACANT. UI never surfaces DIRTY as a required action.
    dirty_tables:      [],         // [{table_id, at}] · derived-only inside tableState()
    unavailable_tables:[],         // [{table_id, reason_code, note, at}] · Part 3+5 UNAVAILABLE
    split_manager_pin: "1234",     // PROTOTYPE-ONLY mock PIN (Part 6). Contract §9 flags manager auth as UNRESOLVED.
    invoice_voids: [],             // Part 6.3 · audit log of INVOICE_VOIDED events [{source_order_id, voided_invoice, authorised_by, at}]
    invoice_replacements: {},      // Part 6.3 · { voided_inv: [child_inv, ...], child_inv: replaced_inv }
    aggregator_processed: {},      // Part 6 · { aggregator_order_id: { action: "accepted"|"rejected", ...meta } }
    temp_tables:       [],         // Part 9 · ephemeral floor entries [{id, label, created_at}]
    open_item_enabled: false,      // Part 8 · tenant-configurable toggle (legacy, retained for backwards state hydration)
    managed_open_items: [],        // Sprint 4 correction · [{id, name, price, category, created_by, created_at}]
    // Sprint 6 correction 4 · outlet-level knobs surfaced on session
    // so tenant-configurable defaults ship one place. Defaults track
    // OUTLET_CONFIG in mockData.js (service charge OFF).
    outlet_config: { ...OUTLET_CONFIG },
    // Sprint 6 correction 1 · runtime tax-rounding mode. Defaults to
    // per_component; the acceptance-pass panel and demo can flip it
    // to total_only to visualise the two-mode difference.
    tax_rounding_mode: tax_rounding_mode,
    // Sprint 7 correction 3 · Staff login binding. Every action that
    // stamps an actor reads getActor(state) so no hardcoded mock id
    // (user_mock_01, mgr_mock_01, s_1) survives once a staff member
    // logs in. `locked` flips true either on manual Lock or on the
    // idle timeout; the terminal wraps its shell in the PIN screen
    // until either flag clears.
    logged_in_staff_id: null,
    logged_in_at:       null,
    locked:             false,
    locked_at:          null,
    idle_lock_seconds:  IDLE_LOCK_SECONDS,
    // ── Sprint 3 · Business day ──────────────────────────────────
    business_date:        null,    // Part 6 · YYYY-MM-DD assigned at open; carried forward
    business_day_opened_at:   null,
    business_day_opened_by:   null,
    business_day_closed_at:   null,
    business_day_closed_by:   null,
    // ── Sprint 3 · Shifts ────────────────────────────────────────
    // { id, opening_float, opened_at, opened_by, closed_at, closed_by,
    //   counted_cash, expected_cash, variance, blind, reason_code,
    //   note, denominations, expected_by_mode }
    shifts:               [],
    // ── Sprint 3 · Reports ──────────────────────────────────────
    x_reports:            [],
    z_reports:            [],      // immutable, sequentially numbered
    z_report_seq:         0,
    // ── Sprint 3 · QSR token queue ──────────────────────────────
    // { number, order_id, state: "preparing"|"ready"|"collected", allocated_at }
    tokens:               [],
    token_seq:            0,
    // ── Sprint 3 · Ticket slip queue (Part 10) ──────────────────
    slip_queue:           [],
    // ── Sprint 2 B.2 · Till (per-staff cash box) ────────────────
    // current_till is null when nobody has opened one for the
    // currently-signed-in staff. When present, carries the server
    // record so the top bar can display the cashier name + float,
    // and the Settle button can gate against it.
    current_till:         null,
    // Snapshot of the LAST closed till so the Z-slip / receipt can
    // display THAT cashier's figures (opening_float, declared,
    // expected, variance) — never mingled with another cashier.
    last_till_summary:    null,
    // On successful STAFF_LOGGED_IN we surface a till-open prompt to
    // eligible staff. Setting this true suppresses the prompt for
    // the current login (steward/bartender / already-open till /
    // dismissed manually).
    till_prompt_dismissed: false,
    // Tenant-scoped advisory — after N hours of an open till the top
    // bar flashes an amber nudge. Server hydration overwrites this via
    // TILL_WARN_HOURS_SET; default matches TILL_OPEN_WARNING_HOURS_DEFAULT.
    till_open_warning_hours: 4,
  },
  orders: {},                      // { [order_id]: { order, lines, payments } }
  active_order_id: null,           // null = floor view; non-null = billing screen for that order
  connection: {
    online: true,
  },
};

// ─── Selectors ──────────────────────────────────────────────────────
/** Returns the active order slice `{order, lines, payments}` or null. */
export const getActive = (state) => {
  if (!state.active_order_id) return null;
  return state.orders[state.active_order_id] || null;
};

/** Part 9 · Returns fixed TABLES + session.temp_tables merged. Temp tables marked `is_temp:true`. */
export const getAllFloorTables = (state, TABLES) => {
  const temps = (state.session.temp_tables || []).map((t) => ({
    id: t.id, label: t.label, capacity: null, zone: "temp", is_temp: true, created_at: t.created_at,
  }));
  return [...TABLES, ...temps];
};

// Part 3 — grace period after settlement, in ms.
export const GRACE_MS = 120_000;

/**
 * Derives table state per Contract §5 with Part 3 amendments:
 *   - DIRTY replaced by a 120s grace period. After 120s the flag is
 *     considered stale and the table is treated as VACANT for the UI.
 *   - UNAVAILABLE takes precedence over everything except an active
 *     order (a table can't be both occupied and unavailable). The
 *     reason string travels with the flag.
 */
/**
 * `tableState` derives the current tile state for a given table_id.
 *
 * Part 2 (Feb 2026) — the tile MUST stay BILLED while a printed
 * invoice is awaiting settlement. Where an order has been split, the
 * tile stays BILLED as long as any child is unsettled and any of them
 * has been printed. Only once every child is SETTLED (or VOIDED)
 * does the tile drop to GRACE and then to VACANT.
 *
 * Empty-parent orders (after ORDER_SPLIT consumed every line) are
 * ignored — they are shells whose content has moved to children.
 */
const isEmptySplitParent = (o) =>
  Array.isArray(o?.order?.split_children) &&
  o.order.split_children.length > 0 &&
  (!Array.isArray(o.lines) || o.lines.length === 0);

export const tableState = (state, table_id, now = Date.now()) => {
  const orders = Object.values(state.orders);
  const activeSet = orders.filter((o) =>
    o.order.table_id === table_id
    && o.order.state !== ORDER_STATE.SETTLED
    && o.order.state !== ORDER_STATE.VOIDED
    && !isEmptySplitParent(o)
  );
  if (activeSet.length > 0) {
    // Part 2 · if ANY relevant order (parent or child) is PRINTED, the
    // tile reads BILLED — the guest still owes money. If every one is
    // pre-print (OPEN or HELD), the tile reads OCCUPIED.
    const anyPrinted = activeSet.some((o) => o.order.state === ORDER_STATE.PRINTED);
    return anyPrinted ? "BILLED" : "OCCUPIED";
  }
  const unavailable = (state.session.unavailable_tables || []).find((u) => u.table_id === table_id);
  if (unavailable) return "UNAVAILABLE";
  const dirtyEntry = state.session.dirty_tables.find((d) => d.table_id === table_id);
  if (dirtyEntry) {
    const age = now - (dirtyEntry.at || 0);
    if (age <= GRACE_MS) return "GRACE";
    // Auto-lapsed → treat as VACANT. Reducer doesn't clean the entry
    // (append-only), but the UI no longer distinguishes it.
    return "VACANT";
  }
  return "VACANT";
};

/** Returns a human-facing string for the table's unavailability, or null. */
export const tableUnavailableReason = (state, table_id) => {
  const u = (state.session.unavailable_tables || []).find((x) => x.table_id === table_id);
  if (!u) return null;
  // Item 4 (Sprint 3 corrections) — the record stores `reason_code`
  // and an optional freeform `note`. Prior code read `u.reason`
  // (undefined) so `currentReason` in the UI was always null, and the
  // "Make available" button never rendered. Now we surface note if
  // present, otherwise the reason code.
  return u.note?.trim() || u.reason_code || null;
};

/** Returns the active order for a table, or null.
 * Empty-parent orders (fully split) are ignored so a follow-up seat
 * flow doesn't pick them up. */
export const activeOrderForTable = (state, table_id) => {
  return Object.values(state.orders).find((o) =>
    o.order.table_id === table_id
    && o.order.state !== ORDER_STATE.SETTLED
    && o.order.state !== ORDER_STATE.VOIDED
    && !isEmptySplitParent(o)
  ) || null;
};

/** Every non-terminal order on a table (parent + children, split-aware). */
export const activeOrdersForTable = (state, table_id) => {
  return Object.values(state.orders).filter((o) =>
    o.order.table_id === table_id
    && o.order.state !== ORDER_STATE.SETTLED
    && o.order.state !== ORDER_STATE.VOIDED
    && !isEmptySplitParent(o)
  );
};

/** Split children of the given parent that are still non-terminal. */
export const outstandingChildrenOf = (state, parent_order_id) => {
  return Object.values(state.orders).filter((o) =>
    o.order.split_from === parent_order_id
    && o.order.state !== ORDER_STATE.SETTLED
    && o.order.state !== ORDER_STATE.VOIDED
  );
};

/** All children (settled or not) of a given parent. */
export const childrenOf = (state, parent_order_id) => {
  return Object.values(state.orders)
    .filter((o) => o.order.split_from === parent_order_id);
};

/** ── Sprint 2 · Staff name resolution (Part 3 defect class) ────────
 * Every surface a HUMAN reads must show the name, never the raw ID.
 * Resolution order:
 *   1. The order's cached `steward_name` (server-resolved on serialise)
 *   2. The currently-signed-in staff (session.staff_snapshot)
 *   3. Session-cached staff directory (session.staff_directory)
 *   4. Demo mockData.STAFF (only relevant on /pos-demo)
 *   5. Fallback "—" (never expose the UUID).
 */
export const staffNameFor = (state, staff_id) => {
  if (!staff_id) return "—";
  // Snapshot on the order itself (LIVE_ORDER_HYDRATED sets this).
  const inActive = state?.active_order_id
    ? state.orders?.[state.active_order_id]?.order?.steward_name
    : null;
  if (inActive && state.orders[state.active_order_id].order.steward_id === staff_id) return inActive;
  // Any other order slice may have this staff cached.
  for (const s of Object.values(state?.orders || {})) {
    if (s?.order?.steward_id === staff_id && s.order.steward_name) return s.order.steward_name;
  }
  // Currently-signed-in staff.
  const snap = state?.session?.staff_snapshot;
  if (snap?.id === staff_id && snap.name) return snap.name;
  // Session-cached directory (populated by hydrate() when possible).
  const dir = state?.session?.staff_directory || {};
  if (dir[staff_id]) return dir[staff_id];
  // Demo fallback.
  const demo = findStaffById(staff_id);
  if (demo?.name) return demo.name;
  return "—";
};

/** Held orders across the whole session, in the order they were held. */
export const heldOrders = (state) => {
  return Object.values(state.orders)
    .filter((o) => o.order.state === ORDER_STATE.HELD)
    .sort((a, b) => (a.order.held_at || 0) - (b.order.held_at || 0));
};

/** ── Sprint 2 · Part C · Unsettled orders queue ────────────────────
 * Every takeaway or delivery order that has had at least one ticket
 * fired but hasn't been settled yet. Includes bills-printed orders
 * (tender is still owed) and held orders (guest paused). This is the
 * queue that reappears on the floor top bar as a count badge and
 * blocks day close.
 *
 * Explicitly EXCLUDES the token/prep queue used by kitchen displays —
 * that's about cooking state (queued → preparing → ready) and moves
 * items in and out based on service. This queue is about MONEY OWED.
 *
 * Prefers server-authoritative figures (`order.server_totals` and
 * `order.first_fired_at`) that LIVE_ORDER_HYDRATED / ORDER_SYNCED_FROM_SERVER
 * now populate. Falls back to the local compute only for LOCAL-ONLY
 * (demo) orders that never touched the server.
 */
const _firstFiredFromLines = (slice) => {
  const fired = (slice.lines || [])
    .filter((l) => l.ticket_id != null && l.ticket_fired_at)
    .map((l) => Number(l.ticket_fired_at));
  return fired.length > 0 ? Math.min(...fired) : null;
};

export const unsettledQueue = (state) => {
  return Object.values(state.orders)
    .filter((o) => {
      const t = o.order.type;
      if (t !== ORDER_TYPE.TAKEAWAY && t !== ORDER_TYPE.DELIVERY) return false;
      if (o.order.state === ORDER_STATE.SETTLED) return false;
      if (o.order.state === ORDER_STATE.VOIDED)  return false;
      // Financial Ground Truth (Feb 2026) · one visible list of every
      // incomplete transaction — OPEN (punching), KOT_FIRED (billing),
      // PRINTED (settlement), HELD (parked). A row disappears only
      // when its order becomes SETTLED, VOIDED, or is fully empty
      // (no lines punched yet). This is the "money owed / work not
      // finished" list the cashier reaches for during service.
      const linesCount = (o.lines || []).filter((l) => !l.voided).length;
      if (linesCount === 0 && o.order.state !== ORDER_STATE.HELD) return false;
      return true;
    })
    .map((o) => {
      // Prefer server timestamps and totals; fall back to line-derived
      // for demo/local-only orders.
      const fired_at = o.order.first_fired_at || _firstFiredFromLines(o);
      const total = o.order.server_totals?.grand_total
        ?? computeTotalsFor(state, o.order.id)?.total
        ?? 0;
      const item_count = (o.lines || [])
        .filter((l) => !l.voided)
        .reduce((n, l) => n + (Number(l.qty) || 0), 0);
      return {
        order_id:      o.order.id,
        type:          o.order.type,
        state:         o.order.state,
        token:         o.order.token || null,
        guest_name:    o.order.guest_name || null,
        guest_phone:   o.order.guest_phone || null,
        first_fired_at: fired_at,
        item_count,
        total,
        printed:       o.order.state === ORDER_STATE.PRINTED
                      || !!o.order.invoice_no,
        held:          o.order.state === ORDER_STATE.HELD,
      };
    })
    .sort((a, b) => (a.first_fired_at || 0) - (b.first_fired_at || 0));
};

// ─── Helpers ────────────────────────────────────────────────────────
const uid = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
const itemById = (id) => ITEMS.find((it) => it.id === id);

/** Line-shape equality — Part 6.1: variants/addons removed. */
const sameLineShape = (l, target) => {
  if (l.item_id !== target.item_id) return false;
  if ((l.note || "") !== (target.note || "")) return false;
  return true;
};

/** True when at least one line on the given slice has no ticket_id yet. */
export const hasUnsent = (slice) => (slice?.lines || []).some((l) => l.ticket_id == null);

/** Aggregate active-slice presentation shim for legacy code paths. */
const emptySlice = { order: { state: null, table_id: null, type: null, invoice_no: null }, lines: [], payments: [] };

/**
 * computeTotals runs against the ACTIVE order slice.
 *
 * Part 5 — order-level discount is stored as either
 *   { type: "percent", value: N }  where N is a percentage 0..100
 *   { type: "absolute", value: N } where N is a rupee amount
 * on order.discount. It is APPORTIONED across the lines proportionally
 * to line net so per-tax-category bases remain correct (food vs
 * liquor tax rates differ).
 *
 * Part 7 — order-level discount EXCLUDES any line that already
 * carries a line-level discount. The rationale is that a line
 * discount is a targeted, per-item override (a manager comp on one
 * dish, for example) — layering a percentage on top would double-dip
 * against the guest's benefit. Excluded lines retain their line
 * discount only; the order percentage/amount is apportioned only
 * across the eligible lines. `computeTotals` also returns
 * `order_discount_excluded_count` and `order_discount_excluded_base`
 * so the DiscountDialog can surface them to the cashier.
 */
export function computeTotals(state, opts) {
  const slice = getActive(state) || emptySlice;
  const sessionOpts = {
    tax_rounding_mode: state?.session?.tax_rounding_mode,
    outlet_config:     state?.session?.outlet_config,
    ...(opts || {}),
  };
  return _computeTotalsForSlice(slice, sessionOpts);
}

/** Compute totals for a specific order id (used by FloorView table
 *  tiles and by the split-children view for per-child balance). */
export function computeTotalsFor(state, order_id, opts) {
  const slice = state.orders?.[order_id] || emptySlice;
  const sessionOpts = {
    tax_rounding_mode: state?.session?.tax_rounding_mode,
    outlet_config:     state?.session?.outlet_config,
    ...(opts || {}),
  };
  return _computeTotalsForSlice(slice, sessionOpts);
}

function _computeTotalsForSlice(slice, opts = {}) {
  const lines = slice.lines || [];
  // Sprint 6 correction 1 · resolve the tax rounding mode. Reader may
  // override for the fixture and for the on-bill preview toggle.
  const rounding = opts.tax_rounding_mode || tax_rounding_mode;
  const outletCfg = { ...OUTLET_CONFIG, ...(opts.outlet_config || {}) };

  // 1) per-line gross and per-line discount, plus the pre-order-disc net.
  const rows = lines.map((l) => {
    const gross  = Number(l.qty) * Number(l.unit_price);
    const lDisc  = Math.min(Number(l.discount || 0), gross);
    const net    = gross - lDisc;
    // Open-item lines carry their own tax_class ("food"|"liquor"). Menu
    // items resolve from ITEMS via category_id (cat_liquor → liquor).
    let cat, tax_class;
    if (l.is_open_item) {
      tax_class = l.open_item_tax_class === "liquor" ? "liquor" : "food";
      cat = tax_class === "liquor" ? "cat_liquor" : "cat_starters";
    } else {
      const it = itemById(l.item_id);
      cat = it?.category_id || "cat_starters";
      tax_class = cat === "cat_liquor" ? "liquor" : "food";
    }
    // Part 7 · a line becomes ineligible for order-level discount as
    // soon as it carries any line-level discount.
    const eligible = lDisc <= 0;
    return { gross, lDisc, net, cat, eligible,
             conf: tax_class === "liquor" ? TAX_CONFIG.liquor : TAX_CONFIG.food };
  });
  const preOrderNet = rows.reduce((s, r) => s + r.net, 0);
  // Part 7 · order-discount base = sum of eligible-line net only.
  const eligibleNet = rows.reduce((s, r) => s + (r.eligible ? r.net : 0), 0);
  const excludedCount = rows.filter((r) => !r.eligible).length;
  const excludedBase  = rows.reduce((s, r) => s + (!r.eligible ? r.net : 0), 0);

  // 2) compute order-level discount amount and apportion across
  //    eligible lines only (Part 7).
  const disc = slice.order?.discount || null;
  let orderDiscAmount = 0;
  if (disc && eligibleNet > 0) {
    if (disc.type === "percent")  orderDiscAmount = eligibleNet * (Math.max(0, Math.min(100, Number(disc.value || 0))) / 100);
    if (disc.type === "absolute") orderDiscAmount = Math.min(Number(disc.value || 0), eligibleNet);
  }

  // 3) tax bases on per-line apportioned net.
  //    Line items NEVER round — full precision preserved so a return
  //    of the exact same line reverses the same rupee value out.
  let subtotal = 0, cgst_raw = 0, sgst_raw = 0, vat_raw = 0, discount = 0;
  let food_net_for_sc = 0, liquor_net_for_sc = 0;
  let has_food = false, has_liquor = false;
  for (const r of rows) {
    // Part 7 · lines carrying a line-level discount receive zero apportion.
    const apport = (r.eligible && eligibleNet > 0)
      ? (r.net / eligibleNet) * orderDiscAmount
      : 0;
    const finalNet = Math.max(0, r.net - apport);
    subtotal += r.gross;
    discount += r.lDisc + apport;
    if (r.cat === "cat_liquor") { has_liquor = true; liquor_net_for_sc += finalNet; }
    else                        { has_food   = true; food_net_for_sc   += finalNet; }
    cgst_raw += finalNet * (r.conf.cgst || 0);
    sgst_raw += finalNet * (r.conf.sgst || 0);
    vat_raw  += finalNet * (r.conf.vat  || 0);
  }
  const net_subtotal = subtotal - discount;

  // Sprint 6 correction 4 · service charge sits BETWEEN discount and
  // tax. Applied to F&B net; liquor excluded unless the outlet has
  // explicitly opted in. Whether the charge itself is taxable is a
  // separate flag — default off, off in this fixture.
  let service_charge = 0;
  if (outletCfg.service_charge_enabled) {
    const sc_base = food_net_for_sc
                  + (outletCfg.service_charge_on_liquor ? liquor_net_for_sc : 0);
    service_charge = sc_base * (Number(outletCfg.service_charge_pct) || 0) / 100;
    if (outletCfg.service_charge_taxable && sc_base > 0) {
      // Tax the service charge under the SAME slab as the base it was
      // computed on. Split proportionally between food (GST) and
      // liquor-if-included (VAT).
      const food_share   = sc_base > 0 ? (food_net_for_sc   / sc_base) : 0;
      const liquor_share = sc_base > 0 ? ((outletCfg.service_charge_on_liquor ? liquor_net_for_sc : 0) / sc_base) : 0;
      cgst_raw += service_charge * food_share   * TAX_CONFIG.food.cgst;
      sgst_raw += service_charge * food_share   * TAX_CONFIG.food.sgst;
      vat_raw  += service_charge * liquor_share * TAX_CONFIG.liquor.vat;
    }
  }

  // Sprint 6 correction 1 · Section 170 / Rule 51.
  //   PER_COMPONENT — each tax component rounds to the nearest rupee
  //     independently. round_off reconciles the summed rounded parts
  //     to the payable amount.
  //   TOTAL_ONLY    — exact paise on tax lines; only the total rounds.
  let cgst, sgst, vat, tax_total;
  if (rounding === TAX_ROUNDING_MODES.PER_COMPONENT) {
    cgst = Math.round(cgst_raw);
    sgst = Math.round(sgst_raw);
    vat  = Math.round(vat_raw);
    tax_total = cgst + sgst + vat;
  } else {
    cgst = cgst_raw;
    sgst = sgst_raw;
    vat  = vat_raw;
    tax_total = cgst_raw + sgst_raw + vat_raw;
  }
  const raw_total    = net_subtotal + service_charge + tax_total;
  const grand_total  = Math.round(raw_total);
  // Sprint 6 correction 1 · round_off is a pure BALANCING figure that
  // reconciles the summed components to the payable amount. It does
  // NOT enter any tax base and must never be added to a component
  // value on the invoice. It exists only on the "Round off" line.
  const round_off    = grand_total - raw_total;
  // Part 4.4 · a pending pay_online tender is NOT paid until confirmed.
  const paid = (slice.payments || [])
    .filter((p) => p.status !== "pending")
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  // Part 4.5 · cash-drawer expectation: only "cash" mode contributes.
  // Card, UPI, aggregators, room posting, due and complimentary are all
  // excluded because the physical cash box never sees them.
  const cash_expected = (slice.payments || [])
    .filter((p) => p.mode === "cash" && p.status !== "pending")
    .reduce((s, p) => s + Number(p.amount || 0), 0);
  const balance = grand_total - paid;
  const local = { subtotal, discount, net_subtotal, cgst, sgst, vat, tax_total,
           grand_total, round_off, paid, balance, has_food, has_liquor,
           cash_expected,
           // Sprint 6 correction 4 · service charge flows on the totals
           // panel + invoice as its own line, hidden entirely when the
           // outlet has it disabled.
           service_charge,
           service_charge_enabled: outletCfg.service_charge_enabled,
           tax_rounding_mode: rounding,
           order_discount: orderDiscAmount,
           order_discount_eligible_base: eligibleNet,
           order_discount_excluded_count: excludedCount,
           order_discount_excluded_base:  excludedBase,
           tax_lines: null,
           calculation_version: null,
           from_server_snapshot: false };

  // ── Financial Hardening (Feb 2026) · server-snapshot verbatim ──
  // Once a printed invoice sits on the order, that invoice is the
  // guest's fiscal record. The client's local arithmetic runs ONLY
  // while the order is OPEN and unprinted (draft view). The moment
  // an invoice exists, the payable / net / tax figures are read
  // verbatim from `order.invoice.totals`. If the local arithmetic
  // later drifts (menu edit, tax-config change, a bug in a helper),
  // the guest still pays what was printed — and the paid/balance
  // are derived against THAT payable, not a locally recomputed one.
  const inv = slice.order?.invoice;
  const it  = inv?.totals;
  if (it && Number.isFinite(Number(it.amount_payable))) {
    const serverPayable = Number(it.amount_payable);
    const byComp = it.tax_by_component || {};
    return {
      ...local,
      grand_total:  serverPayable,
      subtotal:     Number(it.subtotal ?? local.subtotal),
      discount:     Number(it.discount_total ?? local.discount),
      net_subtotal: Number(it.net_amount ?? local.net_subtotal),
      tax_total:    Number(it.tax_total ?? local.tax_total),
      round_off:    Number(it.round_off ?? local.round_off),
      cgst:         Number(byComp.cgst ?? local.cgst),
      sgst:         Number(byComp.sgst ?? local.sgst),
      vat:          Number(byComp.vat  ?? local.vat),
      // Balance is the server payable minus locally-hydrated
      // tenders. Payments in the reducer come from server tender
      // responses (add-tender / LIVE_ORDER_HYDRATED), so this is
      // still a server-authoritative derivation.
      balance:      serverPayable - paid,
      tax_lines:    Array.isArray(it.tax_lines) ? it.tax_lines : null,
      calculation_version:  it.calculation_version || null,
      from_server_snapshot: true,
    };
  }
  return local;
}

// ─── Order-slice sub-reducer ────────────────────────────────────────
// Operates on a single order slice: {order, lines, payments}. Actions
// dispatched while an order is active are routed through here.
function orderReducer(slice, action, session) {
  const { order, lines, payments } = slice;

  switch (action.type) {
    case ACT.ORDER_TYPE_SET: {
      const type = action.payload?.type;
      if (!type) return slice;
      const patch = { type };
      if (type !== ORDER_TYPE.DINE_IN) patch.table_id = null;
      if (type === ORDER_TYPE.TAKEAWAY || type === ORDER_TYPE.DELIVERY || type === ORDER_TYPE.ROOM_SERVICE) {
        if (!order.token) patch.token = `T-${Math.floor(Math.random() * 900) + 100}`;
      }
      return { ...slice, order: { ...order, ...patch } };
    }

    case ACT.TABLE_ASSIGNED: {
      const { table_id } = action.payload || {};
      if (!table_id) return slice;
      return { ...slice, order: { ...order, table_id } };
    }

    case ACT.COVERS_SET: {
      const { covers } = action.payload || {};
      if (covers == null) return slice;
      return { ...slice, order: { ...order, covers: Number(covers) } };
    }

    case ACT.GUEST_DETAILS_UPDATED: {
      // Part 5 · attach / replace guest details on a non-terminal order.
      // Consent timestamp is set the first time any identifying detail
      // is written; on subsequent edits it is refreshed only if the
      // caller opts in (guest re-consented to the update).
      if ([ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      const p = action.payload || {};
      const patch = {};
      if ("guest_phone"   in p) patch.guest_phone   = p.guest_phone   || null;
      if ("guest_name"    in p) patch.guest_name    = p.guest_name    || null;
      if ("guest_address" in p) patch.guest_address = p.guest_address || null;
      if (p.consent_captured_at) patch.guest_consent_at = p.consent_captured_at;
      return { ...slice, order: { ...order, ...patch } };
    }

    case ACT.LINE_ADDED: {
      // payload: { item_id, note, name?, price?, routing? }
      // Sprint 2 B.2: name / price / routing may be supplied on the
      // payload directly (live path passes them so we don't depend on
      // the demo-only ITEMS constant to resolve). If the caller only
      // gave us `item_id`, we fall back to the demo menu.
      if ([ORDER_STATE.PRINTED, ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      if (order.type === ORDER_TYPE.DINE_IN && !order.table_id) return slice;
      if (order.type === ORDER_TYPE.ROOM_SERVICE && !order.table_id) return slice;
      const p = action.payload || {};
      const fromMenu = itemById(p.item_id);
      // Resolve name / price / routing with the caller's snapshot
      // taking precedence over the demo menu — the demo menu can be
      // stale or empty in live mode.
      const resolved = {
        id:      p.item_id,
        name:    p.name    ?? fromMenu?.name,
        price:   p.price   ?? fromMenu?.price,
        routing: p.routing ?? fromMenu?.routing ?? "kitchen",
      };
      if (!resolved.name || resolved.price == null) return slice;
      const target = { item_id: resolved.id, note: p.note || "" };
      // Aggregation rule (Task 1c + Contract §4): repeat taps on an
      // item with IDENTICAL note increment the existing UNFIRED line.
      // A FIRED line (ticket_id set) is never touched — §4 is append-only.
      const existingIdx = lines.findIndex((l) =>
        l.ticket_id == null && sameLineShape(l, target)
      );
      if (existingIdx >= 0) {
        const bumped = { ...lines[existingIdx], qty: (lines[existingIdx].qty || 0) + 1 };
        const next = [...lines];
        next[existingIdx] = bumped;
        return { ...slice, lines: next,
                 order: { ...order, state: ORDER_STATE.OPEN } };
      }
      const new_line = {
        id: p.local_line_id || uid("ln"),
        item_id: resolved.id,
        item_name: resolved.name,
        qty: 1,
        unit_price: Number(resolved.price),
        routing: resolved.routing || "kitchen",
        note:    target.note,
        discount: 0,
        ticket_id:  null,
        is_open_item: false,
      };
      return { ...slice, lines: [...lines, new_line],
               order: { ...order, state: ORDER_STATE.OPEN } };
    }

    case ACT.LINE_ADDED_OPEN: {
      // Part 8 · open item punch. Payload:
      //   { name, price, qty, category, punched_by }
      if ([ORDER_STATE.PRINTED, ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      if (order.type === ORDER_TYPE.DINE_IN && !order.table_id) return slice;
      if (order.type === ORDER_TYPE.ROOM_SERVICE && !order.table_id) return slice;
      const p = action.payload || {};
      const price = Number(p.price) || 0;
      const qty = Math.max(1, Number(p.qty) || 1);
      if (!p.name || price <= 0) return slice;
      const cat = ["food", "beverage", "alcohol", "other"].includes(p.category) ? p.category : "food";
      // Category → tax_class + routing.
      const tax_class = cat === "alcohol" ? "liquor" : "food";
      const routing = (cat === "alcohol" || cat === "beverage") ? "bar" : "kitchen";
      const new_line = {
        id: uid("ln"),
        item_id: `open_${Math.random().toString(36).slice(2, 8)}`,
        item_name: p.name.trim(),
        qty, unit_price: price,
        routing, note: "",
        discount: 0,
        ticket_id: null,
        is_open_item: true,
        open_item_category: cat,
        open_item_tax_class: tax_class,
        punched_by: p.punched_by || "system",
        punched_at: Date.now(),
      };
      return { ...slice, lines: [...lines, new_line],
               order: { ...order, state: ORDER_STATE.OPEN } };
    }

    case ACT.LINE_QTY_CHANGED: {
      if ([ORDER_STATE.PRINTED, ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      const { line_id, qty } = action.payload || {};
      const target = lines.find((l) => l.id === line_id);
      if (!target) return slice;
      const new_qty = Math.max(1, Number(qty) || 1);
      const old_qty = Number(target.qty) || 0;
      if (new_qty === old_qty) return slice;

      // Unfired line: mutate qty in place.
      if (target.ticket_id == null) {
        const next = lines.map((l) => l.id === line_id ? { ...l, qty: new_qty } : l);
        return { ...slice, lines: next, order: { ...order, state: ORDER_STATE.OPEN } };
      }

      // Fired line, qty INCREASED: original stays; delta as new line.
      if (new_qty > old_qty) {
        const delta = new_qty - old_qty;
        const split_line = {
          id: uid("ln"), item_id: target.item_id, item_name: target.item_name,
          qty: delta, unit_price: target.unit_price,
          routing: target.routing || routingForItem(target.item_id),
          note: "", discount: 0, ticket_id: null,
          is_open_item: !!target.is_open_item,
          open_item_category: target.open_item_category,
          open_item_tax_class: target.open_item_tax_class,
          punched_by: target.punched_by, punched_at: target.punched_at,
        };
        const idx = lines.findIndex((l) => l.id === line_id);
        const next = [...lines.slice(0, idx + 1), split_line, ...lines.slice(idx + 1)];
        return { ...slice, lines: next, order: { ...order, state: ORDER_STATE.OPEN } };
      }

      // Fired line, qty DECREASED: emit TICKET_ITEM_CANCELLED to session.
      const cancel_delta = old_qty - new_qty;
      const cancel_event = {
        type: "TICKET_ITEM_CANCELLED",
        ticket_id: target.ticket_id,
        ticket_dest: target.ticket_dest,
        line_id: target.id, item_name: target.item_name,
        qty_cancelled: cancel_delta,
        reason_code:   action.payload?.reason_code   || "GUEST_CHANGED_MIND",
        note:          action.payload?.note          || "",
        authorised_by: action.payload?.authorised_by || "system",
        at: Date.now(),
      };
      const next_lines = lines.map((l) => l.id === line_id ? { ...l, qty: new_qty } : l);
      // Return a marker on the slice so the outer reducer can push
      // the cancellation event onto session.ticket_cancellations.
      return {
        ...slice, lines: next_lines,
        order: { ...order, state: ORDER_STATE.OPEN },
        _pending_cancellation: cancel_event,
      };
    }

    case ACT.LINE_NOTE_SET: {
      // Task 1c: kitchen notes are editable ONLY while a line is
      // unfired. The kitchen ticket for a fired line has already been
      // printed — editing the note wouldn't reach them.
      if ([ORDER_STATE.PRINTED, ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      const { line_id, note } = action.payload || {};
      const target = lines.find((l) => l.id === line_id);
      if (!target) return slice;
      if (target.ticket_id != null) return slice;
      const next = lines.map((l) => l.id === line_id ? { ...l, note: note || "" } : l);
      return { ...slice, lines: next };
    }

    case ACT.LINE_DISCOUNT_SET: {
      if ([ORDER_STATE.PRINTED, ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      const { line_id, discount } = action.payload || {};
      const next = lines.map((l) => l.id === line_id ? { ...l, discount: Number(discount) || 0 } : l);
      return { ...slice, lines: next };
    }

    case ACT.LINE_VOIDED: {
      if ([ORDER_STATE.PRINTED, ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      const { line_id } = action.payload || {};
      const next = lines.filter((l) => l.id !== line_id);
      return { ...slice, lines: next };
    }

    case ACT.TICKET_SENT: {
      // Part 7 · fire one ticket per destination present among unfired lines.
      if (!hasUnsent(slice)) return slice;
      const unfired = lines.filter((l) => l.ticket_id == null);
      const dests = Array.from(new Set(unfired.map((l) => l.routing || "kitchen")));
      const fired_at = Date.now();
      let ktrack = session.ticket_seq?.kitchen || 0;
      let btrack = session.ticket_seq?.bar     || 0;
      const dest_seq = {};
      dests.forEach((d) => {
        if (d === "bar")     dest_seq[d] = ++btrack;
        else                 dest_seq[d] = ++ktrack;
      });
      const next_lines = lines.map((l) => {
        if (l.ticket_id != null) return l;
        const d = l.routing || "kitchen";
        const label = (d === "bar" ? "BOT_" : "KOT_") + dest_seq[d];
        return { ...l, ticket_id: label, ticket_dest: d, ticket_fired_at: fired_at };
      });
      // Part 10 (Sprint 3) · emit one slip per fired destination.
      // Sprint 2 correction Part 3 · resolve the steward NAME here so
      // slips (and any printed thermal record) never leak a raw UUID.
      // `orderReducer` doesn't receive the full reducer state, so we
      // resolve using the session snapshot first (covers the common
      // case — the firing staff is the current session), and fall
      // through to the demo staff dictionary otherwise. If neither
      // resolves, we emit null and the slip renders "—" — never the
      // raw ID.
      const stewardName =
           (order.steward_name)
        || (session?.staff_snapshot && session.staff_snapshot.id === order.steward_id
              ? session.staff_snapshot.name : null)
        || (findStaffById(order.steward_id)?.name)
        || null;
      const slips = dests.map((d) => {
        const label = (d === "bar" ? "BOT_" : "KOT_") + dest_seq[d];
        const dest_lines = next_lines.filter((l) => l.ticket_id === label);
        return {
          id: label,
          kind: "fire",
          ticket_id: label,
          dest: d,
          at: fired_at,
          type: order.type,
          covers: order.covers,
          table_label: tableLabelFor(order.table_id),
          token: order.token,
          steward_id: order.steward_id,
          // Sprint 2 correction Part 3 · resolved staff name travels
          // with the slip. TicketSlipDialog and the printed slip
          // display this — never the raw ID.
          steward_name: stewardName,
          lines: dest_lines.map((l) => ({
            item_name: l.item_name, qty: l.qty, note: l.note,
          })),
        };
      });
      return { ...slice, lines: next_lines,
               order: { ...order, state: ORDER_STATE.OPEN },
               _pending_ticket_seq: { kitchen: ktrack, bar: btrack },
               _pending_slips: slips };
    }

    case ACT.BILL_PRINTED: {
      if (lines.length === 0) return slice;
      if (hasUnsent(slice)) return slice;
      if (order.state === ORDER_STATE.VOIDED) return slice;
      if (order.invoice_no) {
        return { ...slice, order: { ...order, state: ORDER_STATE.PRINTED } };
      }
      // Sprint 3B · The server allocates the fiscal invoice number
      // (contract §7). When livePos.printBill has already made the
      // round-trip, the caller passes the real invoice_no through the
      // payload — we adopt it verbatim rather than re-allocating locally.
      // The old local allocator is kept only for /pos-demo (no session).
      const server_no = action.payload?.invoice_no;
      const server_inv = action.payload?.invoice;
      if (server_no) {
        let linkage = null;
        if (order.voided_invoice || order.parent_voided_invoice) {
          linkage = { child: server_no, replaced: order.voided_invoice || order.parent_voided_invoice };
        }
        return { ...slice,
                 order: { ...order, state: ORDER_STATE.PRINTED,
                          invoice_no: server_no,
                          invoice:    server_inv || null },
                 _pending_linkage: linkage };
      }
      const next_seq = (session.invoice_seq || 0) + 1;
      const inv_no = `${session.outlet_code}/${session.fy}/${String(next_seq).padStart(3, "0")}`;
      // Part 6.3 · bidirectional invoice linkage for post-print splits.
      // If this order was reopened by INVOICE_VOIDED, its
      // `voided_invoice` refers to the original invoice; new invoices
      // on children of that voided one record both directions.
      let linkage = null;
      if (order.voided_invoice || order.parent_voided_invoice) {
        linkage = { child: inv_no, replaced: order.voided_invoice || order.parent_voided_invoice };
      }
      return { ...slice,
               order: { ...order, state: ORDER_STATE.PRINTED, invoice_no: inv_no },
               _pending_invoice_seq: next_seq,
               _pending_linkage: linkage };
    }

    case ACT.PAYMENT_TENDERED: {
      if (order.state !== ORDER_STATE.PRINTED) return slice;
      const p = action.payload || {};
      if (!(Number(p.amount) > 0) || !p.mode) return slice;
      const payment = {
        // Sprint 5 correction · caller may pass an id so the follow-up
        // PAYMENT_REFERENCE_UPDATED can attach a captured reference to
        // this exact tender.
        id: p.id || uid("pay"),
        mode: p.mode,
        amount: Number(p.amount),
        // Financial Ground Truth (Feb 2026) · carry the server's
        // amount_received + change_due through so the payment overlay
        // + settled receipt can show the exact figures the guest
        // physically handed over vs what was applied to the invoice.
        amount_received: (p.amount_received != null
                          ? Number(p.amount_received)
                          : Number(p.amount)),
        change_due:      Number(p.change_due) || 0,
        reference: (p.reference || "").trim() || null,
        status: p.status || "confirmed",   // Part 4.4 · pay_online sets "pending"
        // Sprint 4 Part 5 · complimentary tenders carry authoriser +
        // reason so the exception report can attribute revenue
        // written off correctly.
        authorised_by: p.authorised_by || null,
        comp_reason:   p.comp_reason   || null,
        at: Date.now(),
      };
      // If this is a complimentary tender, also stamp the parent
      // order with comp_reason + comp_authorised_by so ExceptionReport
      // sees the same authoriser it uses for voids and discounts.
      const isComp = p.mode === "complimentary";
      const orderPatch = isComp
        ? { comp_reason: p.comp_reason || "MANAGER_DISCRETION",
            comp_authorised_by: p.authorised_by || "system" }
        : {};
      return {
        ...slice,
        payments: [...payments, payment],
        order:    { ...order, ...orderPatch },
      };
    }

    case ACT.PAYMENT_STATUS_UPDATED: {
      // Part 4.4 · flip a pending pay_online tender to confirmed.
      const { payment_id, status } = action.payload || {};
      if (!payment_id || !status) return slice;
      const next = payments.map((p) => p.id === payment_id ? { ...p, status } : p);
      return { ...slice, payments: next };
    }

    case ACT.PAYMENT_REFERENCE_UPDATED: {
      // Sprint 5 correction · attach a captured reference to a tender
      // that was created moments earlier. Used by the settlement
      // keyboard chain: Enter tenders → focus jumps to the reference
      // field → Enter accepts the reference → completes if balance is
      // zero.
      const { payment_id, reference } = action.payload || {};
      if (!payment_id) return slice;
      const next = payments.map((p) =>
        p.id === payment_id
          ? { ...p, reference: (reference || "").trim() }
          : p
      );
      return { ...slice, payments: next };
    }

    case ACT.PAYMENT_REVERSED: {
      const { payment_id } = action.payload || {};
      return { ...slice, payments: payments.filter((x) => x.id !== payment_id) };
    }

    case ACT.ORDER_SETTLED: {
      // Part 2 · sub-reducer just flips state to SETTLED. The outer
      // reducer inspects the full orders map afterwards to decide
      // whether to push a dirty_tables entry (only when no siblings
      // or children remain non-terminal on this table).
      const next_order = { ...order, state: ORDER_STATE.SETTLED };
      const next_slice_settled = { ...slice, order: next_order };
      if (order.type !== ORDER_TYPE.DINE_IN || !order.table_id) {
        return next_slice_settled;
      }
      return { ...next_slice_settled, _pending_settled_table: order.table_id };
    }

    case ACT.ORDER_VOIDED: {
      if ([ORDER_STATE.SETTLED, ORDER_STATE.VOIDED].includes(order.state)) return slice;
      const p = action.payload || {};
      return { ...slice, order: {
        ...order,
        state: ORDER_STATE.VOIDED,
        void_reason:        p.reason_code   || "GUEST_ABANDONED",
        void_authorised_by: p.authorised_by || "system",
        void_at:            Date.now(),
      }};
    }

    case ACT.ORDER_HELD: {
      if (order.state !== ORDER_STATE.OPEN) return slice;
      return { ...slice, order: { ...order, state: ORDER_STATE.HELD, held_at: Date.now() } };
    }

    case ACT.ORDER_RESUMED: {
      if (order.state !== ORDER_STATE.HELD) return slice;
      return { ...slice, order: { ...order, state: ORDER_STATE.OPEN, held_at: null } };
    }

    case ACT.DISCOUNT_APPLIED: {
      // Part 5: order-level discount. Editable only while OPEN.
      // Payload { type: 'percent'|'absolute', value: number, reason?, authorised_by? }
      // or payload null/empty to clear.
      if (order.state !== ORDER_STATE.OPEN) return slice;
      const p = action.payload;
      if (!p || !p.type || Number(p.value) <= 0) {
        return { ...slice, order: { ...order, discount: null } };
      }
      return { ...slice, order: { ...order,
        discount: {
          type: p.type,
          value: Number(p.value),
          reason: p.reason || "MANAGER_DISCRETION",
          authorised_by: p.authorised_by || "system",
          at: Date.now(),
        },
      }};
    }

    case ACT.BILL_REPRINTED: {
      // Part 8: reprint. Order must already be PRINTED (invoice
      // allocated). Same invoice_no is preserved; reprint counter
      // bumps for the audit strip on the preview.
      if (order.state !== ORDER_STATE.PRINTED && order.state !== ORDER_STATE.SETTLED) return slice;
      if (!order.invoice_no) return slice;
      return { ...slice, order: { ...order, reprint_count: (order.reprint_count || 0) + 1, last_reprint_at: Date.now() } };
    }

    default:
      return slice;
  }
}

// ─── Root reducer ───────────────────────────────────────────────────
export function posReducer(state, action) {
  // Session gate — refuse every billing action when no shift is open or
  // no staff is signed in. Non-billing actions (login, shift open,
  // navigation, connection toggle, etc.) pass through unaffected.
  if (BILLING_ACTIONS.has(action.type)) {
    const guard = sessionGuard(state);
    if (!guard.ok) {
      // Stamp the refusal onto state so the UI can surface a single,
      // authoritative reason string in the prompt.
      return {
        ...state,
        session: { ...state.session, session_error: guard.message },
      };
    }
  }
  // A steward can never open a shift. Enforcement lives here so a
  // buggy UI never lets them.
  if (action.type === ACT.SHIFT_OPENED) {
    const actor = getActor(state);
    if (actor.role === "steward") {
      return {
        ...state,
        session: { ...state.session,
                   session_error: "A steward cannot open a shift. Ask a cashier or manager." },
      };
    }
  }
  switch (action.type) {

    case ACT.CONNECTION_TOGGLED: {
      return { ...state, connection: { ...state.connection, online: !state.connection.online } };
    }

    // ─── Sprint 2 Checkpoint B.2 · Live persistence ────────────────
    // Every handler below is a no-op unless the target order exists.
    // These actions never CREATE orders — only reconcile / rollback /
    // mark pending on existing ones.
    case ACT.ORDER_PENDING_SET: {
      const { order_id, event_id } = action.payload || {};
      const slice = state.orders[order_id];
      if (!slice) return state;
      return {
        ...state,
        orders: {
          ...state.orders,
          [order_id]: { ...slice, _pending: { event_id, at: Date.now() } },
        },
      };
    }
    case ACT.ORDER_PENDING_CLEAR: {
      const { order_id } = action.payload || {};
      const slice = state.orders[order_id];
      if (!slice) return state;
      const { _pending, ...rest } = slice;
      void _pending;
      return { ...state, orders: { ...state.orders, [order_id]: rest } };
    }
    case ACT.LINE_PENDING_SET: {
      const { order_id, line_id, event_id } = action.payload || {};
      const slice = state.orders[order_id];
      if (!slice) return state;
      const lines = (slice.lines || []).map((l) =>
        l.id === line_id ? { ...l, _pending: { event_id, at: Date.now() } } : l);
      return { ...state, orders: { ...state.orders, [order_id]: { ...slice, lines } } };
    }
    case ACT.LINE_PENDING_CLEAR: {
      const { order_id, line_id } = action.payload || {};
      const slice = state.orders[order_id];
      if (!slice) return state;
      const lines = (slice.lines || []).map((l) => {
        if (l.id !== line_id) return l;
        const { _pending, ...rest } = l;
        void _pending;
        return rest;
      });
      return { ...state, orders: { ...state.orders, [order_id]: { ...slice, lines } } };
    }
    case ACT.LINE_ID_REBOUND: {
      // Rename an optimistic local line id (e.g. `ln_abc123`) to the
      // server's authoritative line id from the API response. Preserves
      // every other field on the line so downstream references (bill
      // preview totals, ticket linkage) stay intact.
      const { order_id, local_line_id, server_line_id } = action.payload || {};
      const slice = state.orders[order_id];
      if (!slice || local_line_id === server_line_id) return state;
      const lines = (slice.lines || []).map((l) =>
        l.id === local_line_id ? { ...l, id: server_line_id } : l);
      return { ...state, orders: { ...state.orders, [order_id]: { ...slice, lines } } };
    }
    case ACT.ORDER_SYNCED_FROM_SERVER: {
      // Merge the server's authoritative fields onto the optimistic
      // slice. We DO NOT wholesale replace: the reducer holds many UI-
      // only fields (grace timers, session-local ids for tokens, etc.)
      // and the server payload uses a slightly different shape. Only
      // fields that map cleanly are copied. The server line list is
      // authoritative for line contents, but we preserve any
      // `_pending` markers still hanging on lines the caller is about
      // to clear.
      const so = action.payload?.server_order;
      if (!so || !so.id) return state;
      const existing = state.orders[so.id];
      if (!existing) return state;
      const serverLinesById = {};
      (so.lines || []).forEach((sl) => { serverLinesById[sl.line_id] = sl; });
      // Build the merged line list: keep original client ordering
      // (which is how the cashier sees them on the panel), but pick up
      // authoritative price / qty / ticket_id from the server. Lines
      // absent from the server response were voided or never persisted
      // — drop them.
      const merged = (existing.lines || []).flatMap((cl) => {
        const sl = serverLinesById[cl.id];
        if (!sl) return [];   // dropped by the server
        delete serverLinesById[cl.id];
        return [{
          ...cl,
          qty:              sl.qty,
          unit_price:       sl.rate_snapshot,
          ticket_id:        sl.ticket_id || null,
          discount:         Number(sl.discount) || 0,
          // Sprint 2 correction Part 1 · pull ticket fired timestamp
          // from the server so the queue's elapsed column updates
          // immediately after firing (no need to reload).
          ticket_fired_at:  sl.ticket_fired_at ? Date.parse(sl.ticket_fired_at) : (cl.ticket_fired_at || null),
          ticket_dest:      sl.ticket_dest || cl.ticket_dest || null,
          ticket_no:        sl.ticket_no || cl.ticket_no || null,
          tax:              sl.tax || cl.tax || null,
          rate_snapshot:    sl.rate_snapshot,
        }];
      });
      // Any server lines the client didn't know about — append. This
      // happens after a repeat-tap that the server merged into an
      // existing row and we later resurrected via a full sync.
      Object.values(serverLinesById).forEach((sl) => {
        merged.push({
          id: sl.line_id,
          item_id: sl.item_id,
          item_name: sl.item_name_snapshot,
          qty: sl.qty,
          unit_price: sl.rate_snapshot,
          routing: sl.routing || "kitchen",
          note: sl.kitchen_note || "",
          discount: Number(sl.discount) || 0,
          ticket_id: sl.ticket_id || null,
          ticket_fired_at:  sl.ticket_fired_at ? Date.parse(sl.ticket_fired_at) : null,
          ticket_dest:      sl.ticket_dest || null,
          ticket_no:        sl.ticket_no || null,
          tax:              sl.tax || null,
          rate_snapshot:    sl.rate_snapshot,
        });
      });
      return {
        ...state,
        orders: {
          ...state.orders,
          [so.id]: {
            ...existing,
            lines: merged,
            order: {
              ...existing.order,
              id: so.id,
              // Server exposes `held: bool` (not a state), so translate:
              // held=true → HELD, held=false with existing HELD → OPEN.
              // Otherwise we leave the reducer's state alone — the client
              // is authoritative for local sub-states (PRINTED, SETTLED)
              // that the server does not model yet in Sprint 2.
              state: so.held === true
                ? ORDER_STATE.HELD
                : (so.held === false && existing.order.state === ORDER_STATE.HELD
                    ? ORDER_STATE.OPEN
                    : existing.order.state),
              // Sprint 2 correction Part 1 · propagate server totals
              // and first_fired_at on every sync so the tile/queue
              // reflect the server truth after every mutation.
              server_totals: so.totals || existing.order.server_totals || null,
              first_fired_at: so.first_fired_at
                ? Date.parse(so.first_fired_at)
                : (existing.order.first_fired_at || null),
              // Refresh the staff name if the server re-resolved it.
              steward_name: so.opened_by_staff_name || existing.order.steward_name || null,
              server_updated_at: so.updated_at,
              _error: null,
            },
          },
        },
      };
    }
    case ACT.SLICE_ROLLBACK: {
      // Restore a pre-optimistic snapshot. Called by posLiveActions
      // when the server refuses a mutation. The snapshot is captured
      // BEFORE the optimistic dispatch, so restoring it is a bit-for-
      // bit revert of that intent.
      const { order_id, snapshot } = action.payload || {};
      if (!order_id || !snapshot) return state;
      return { ...state, orders: { ...state.orders, [order_id]: snapshot } };
    }
    case ACT.ORDER_ERROR_SET: {
      const { order_id, reason } = action.payload || {};
      const slice = state.orders[order_id];
      if (!slice) return state;
      return {
        ...state,
        orders: {
          ...state.orders,
          [order_id]: {
            ...slice,
            order: { ...slice.order, _error: { reason, at: Date.now() } },
          },
        },
      };
    }
    case ACT.ORDER_ERROR_CLEAR: {
      const { order_id } = action.payload || {};
      const slice = state.orders[order_id];
      if (!slice) return state;
      return {
        ...state,
        orders: {
          ...state.orders,
          [order_id]: { ...slice, order: { ...slice.order, _error: null } },
        },
      };
    }

    case ACT.SESSION_HYDRATED: {
      // POS Lifecycle Final Fix · SERVER IS AUTHORITATIVE.
      // On terminal mount / reload / post-login / post-close /
      // post-open, this reducer case MIRRORS the server response
      // exactly. If the server has no business_day, we CLEAR the
      // local business_date — the "Last closed business day: 2026-08-10"
      // ghost was caused by the reducer previously preserving stale
      // local state when SESSION_HYDRATED was fired with no
      // business_day payload.
      const p = action.payload || {};
      const dayFromServer = p.business_day; // may be null / undefined
      const shift = p.shift;
      const patch = {};

      // The caller is expected to invoke SESSION_HYDRATED with the
      // FULL server response (as returned by /pos/session-status);
      // that's the only way we can tell "server has no business day"
      // from "hydrate call didn't include the business_day slot". A
      // partial payload (e.g. just { shift }) skips the day sync.
      if ("business_day" in p) {
        if (dayFromServer && dayFromServer.business_date) {
          patch.business_date          = dayFromServer.business_date;
          patch.business_day_id        = dayFromServer.id || null;
          patch.business_day_opened_at = dayFromServer.opened_at ? Date.parse(dayFromServer.opened_at) : Date.now();
          patch.business_day_opened_by = dayFromServer.opened_by_name
            || (dayFromServer.opened_by ? String(dayFromServer.opened_by).slice(0, 8) : "—");
          patch.business_day_closed_at = null;
          patch.business_day_closed_by = null;
        } else {
          // Server has NO open business day → clear the local slot.
          patch.business_date          = null;
          patch.business_day_id        = null;
          patch.business_day_opened_at = null;
          patch.business_day_opened_by = null;
        }
      }
      // POS Lifecycle Final Fix · the server tells us the outlet's
      // most recent closed date on session-status, so the reducer's
      // `last_closed_business_date` is now server-truth (was previously
      // set only by the reducer's own BUSINESS_DAY_CLOSED handler,
      // producing phantom "next-day" values on refresh).
      if ("last_closed_business_date" in p) {
        patch.last_closed_business_date = p.last_closed_business_date || null;
      }
      const shifts = state.session.shifts || [];
      const hasOpenShift = shifts.some((s) => !s.closed_at);
      if (shift && !hasOpenShift) {
        patch.shifts = [...shifts, {
          id:            shift.id || uid("shift"),
          server_id:     shift.id,
          opening_float: Number(shift.opening_float) || 0,
          opened_at:     shift.opened_at ? Date.parse(shift.opened_at) : Date.now(),
          opened_by:     shift.opened_by_name
                          || (shift.opened_by ? String(shift.opened_by).slice(0, 8) : "—"),
          closed_at:     null,
          closed_by:     null,
          blind:         !!shift.blind,
          counted_cash:  null,
          expected_cash: null,
          variance:      null,
          reason_code:   null,
          note:          null,
          denominations: null,
          expected_by_mode: null,
        }];
        patch.session_error = null;
      }
      if (Object.keys(patch).length === 0
          && !action.payload?.outlet_name) return state;
      // Sprint 2 Consolidation Part 7 · carry the resolved outlet
      // display NAME onto the session so X/Z reports never fall back
      // to `outlet_code` (`WE`, `RB`).
      if (action.payload?.outlet_name) {
        patch.outlet_name = action.payload.outlet_name;
      }
      return { ...state, session: { ...state.session, ...patch } };
    }

    case ACT.SESSION_IDLE_LOCK_SET: {
      // Sprint 3B correction · idle-lock now server-configured per
      // outlet (pos_settings.idle_lock_seconds). Applied on mount /
      // reload; the POSTerminal timer picks it up because the effect
      // depends on `state.session.idle_lock_seconds`.
      const secs = Number(action.payload?.seconds);
      if (!Number.isFinite(secs) || secs < 60) return state;
      if (state.session.idle_lock_seconds === secs) return state;
      return { ...state, session: { ...state.session, idle_lock_seconds: secs } };
    }

    case ACT.SESSION_ROUNDING_SET: {
      // Financial Ground Truth (Feb 2026) · mirror the outlet's
      // invoice_config.rounding_mode onto the session so the OPEN-order
      // preview computes with the same pipeline the server will apply
      // at print-bill time. Draft grand_total, printed amount_payable,
      // payment overlay total, settlement target, X-report contribution
      // and reloaded invoice must ALL agree to the paise.
      const mode = String(action.payload?.mode || "").toLowerCase();
      if (mode !== "total_only" && mode !== "per_component") return state;
      if (state.session.tax_rounding_mode === mode) return state;
      return { ...state, session: { ...state.session, tax_rounding_mode: mode } };
    }

    case ACT.LIVE_ORDER_HYDRATED: {
      // Rebuild a reducer slice from the SERVER's canonical order
      // shape (as returned by GET /api/pos/orders?state=OPEN).
      // Idempotent — if the order is already in the reducer, we
      // overwrite it with the server view.
      const so = action.payload?.server_order;
      if (!so || !so.id) return state;
      const lines = (so.lines || [])
        .filter((sl) => !sl.voided)
        .map((sl) => ({
          id:               sl.line_id,
          item_id:          sl.item_id,
          item_name:        sl.item_name_snapshot,
          qty:              sl.qty,
          unit_price:       sl.rate_snapshot,
          routing:          sl.routing || "kitchen",
          note:             sl.kitchen_note || "",
          discount:         Number(sl.discount) || 0,
          ticket_id:        sl.ticket_id || null,
          // Sprint 2 correction Part 1 · server-authoritative ticket
          // fired timestamp. Unsettled Orders Queue elapsed-time and
          // KDS depend on this. Without it, elapsed shows "—" for
          // every live order.
          ticket_fired_at:  sl.ticket_fired_at ? Date.parse(sl.ticket_fired_at) : null,
          ticket_dest:      sl.ticket_dest || null,
          ticket_no:        sl.ticket_no || null,
          // Also carry tax so per-line/per-order totals reconcile
          // exactly to the server figures — no more client re-compute
          // against a demo menu.
          tax:              sl.tax || null,
          tax_rate:         sl.tax_rate ?? null,
          tax_regime:       sl.tax_regime ?? null,
          rate_snapshot:    sl.rate_snapshot,
        }));
      const slice = {
        order: {
          id:            so.id,
          // held=true → HELD; server state PRINTED / SETTLED / VOIDED
          // map 1:1 (Sprint 3B fiscal path); OPEN is the default.
          state:         so.held === true ? ORDER_STATE.HELD
                        : so.state === "PRINTED" ? ORDER_STATE.PRINTED
                        : so.state === "SETTLED" ? ORDER_STATE.SETTLED
                        : so.state === "VOIDED"  ? ORDER_STATE.VOIDED
                        : ORDER_STATE.OPEN,
          type:          so.order_type || ORDER_TYPE.DINE_IN,
          table_id:      so.table_id || null,
          token:         so.token || null,
          covers:        so.covers ?? null,
          steward_id:    so.opened_by_staff_id || so.steward_staff_id || so.created_by || getActor(state).id,
          // Sprint 2 correction Part 3 · server-resolved staff name.
          // Every human-facing surface (tile initials, slip STEWARD,
          // bill preview) must read this — never the raw UUID.
          steward_name:  so.opened_by_staff_name || null,
          guest_name:    so.guest_name || null,
          guest_phone:   so.guest_phone || null,
          guest_address: so.guest_address || null,
          opened_at:     so.opened_at ? Date.parse(so.opened_at) : Date.now(),
          // Sprint 2 correction Part 1 · earliest fired ticket for
          // the whole order. Unsettled queue elapsed column reads it.
          first_fired_at: so.first_fired_at ? Date.parse(so.first_fired_at) : null,
          // Sprint 2 correction Part 1 · server-authoritative totals.
          // The Unsettled queue row and floor tiles use these directly
          // so the numbers on-screen always match what the server
          // would bill.
          server_totals: so.totals || null,
          server_updated_at: so.updated_at,
          // Sprint 3B · a browser reload must find PRINTED / SETTLED
          // orders with their fiscal invoice + tenders intact.
          invoice_no: so.invoice_no || null,
          invoice:    so.invoice   || null,
          void_reason: null, void_authorised_by: null, void_at: null,
          // POS Control Sprint · preserve split lineage through
          // hydration so the SplitChildrenView finds children on
          // parent orders after reload / server round-trips.
          split_from:     so.split_from     || null,
          split_label:    so.split_label    || null,
          split_children: Array.isArray(so.split_children) ? so.split_children : [],
          _error: null,
        },
        lines,
        // Sprint 3B · server-side captured tenders survive reload.
        // Reversed tenders stay in the array (append-only) but the
        // reducer's payments consumer filters out `status: "reversed"`.
        payments: (so.tenders || []).map((t) => ({
          id:         t.id,
          mode:       t.mode,
          amount:     Number(t.amount) || 0,
          // Financial Ground Truth (Feb 2026) · propagate server change
          // fields onto rehydrated tenders so a browser refresh keeps
          // showing the correct "cash received / change due" figures.
          amount_received: Number(t.amount_received) || Number(t.amount) || 0,
          change_due:      Number(t.change_due) || 0,
          reference:  t.reference || null,
          status:     t.status === "captured" ? "confirmed" : t.status,
          authorised_by: t.comp_authorised_by || null,
          comp_reason:   t.comp_reason || null,
          at:         t.captured_at ? Date.parse(t.captured_at) : Date.now(),
        })).filter((p) => p.status !== "reversed"),
      };
      return {
        ...state,
        orders: { ...state.orders, [so.id]: slice },
      };
    }

    case ACT.DEMO_RESET: {
      // Sprint 6 correction 5 · full state swap for /pos-demo. Payload
      // carries the pre-built snapshot so the caller decides what
      // "reset" means. Never used by the authenticated terminal.
      const snap = action.payload?.snapshot;
      return snap || state;
    }

    case ACT.STAFF_LOGGED_IN: {
      const p = action.payload || {};
      // Sprint-1 backend integration · the login payload now carries
      // the full staff record from either the mock findStaffByPin path
      // (demo mode) or the /api/pos/auth/login response (real mode).
      // We stash it in state.session.staff_snapshot so the terminal
      // top bar and every actor stamp can resolve the identity without
      // going back to mockData.STAFF (which won't contain real staff).
      const staff = p.staff || findStaffById(p.staff_id);
      if (!staff) return state;
      // Sprint 2 B.2 · a NEW staff (id changed vs previously signed in)
      // starts with a clean till slate. The previous cashier's till
      // belongs to them alone — the incoming user must open their own.
      // Same staff unlocking after their own lock keeps their till.
      const sameStaff = state.session.logged_in_staff_id === staff.id;
      return {
        ...state,
        session: {
          ...state.session,
          logged_in_staff_id: staff.id,
          logged_in_at:       Date.now(),
          locked:             false,
          locked_at:          null,
          staff_snapshot:     staff,
          session_error:      null,
          // Reset till state on staff swap; preserve on same-staff unlock.
          current_till:          sameStaff ? state.session.current_till          : null,
          last_till_summary:     sameStaff ? state.session.last_till_summary     : null,
          till_prompt_dismissed: false,
        },
      };
    }
    case ACT.STAFF_LOCKED: {
      // Lock clears the auth token — a shared terminal must never let
      // an unlocking staff member inherit the previous session. Any
      // open shift and open orders survive; only identity does not.
      try {
        if (typeof window !== "undefined") window.localStorage.removeItem("pos_token");
      } catch (_) { /* ignore */ }
      return { ...state, session: { ...state.session, locked: true, locked_at: Date.now(),
                                    session_error: null } };
    }
    case ACT.STAFF_LOGGED_OUT: {
      try {
        if (typeof window !== "undefined") window.localStorage.removeItem("pos_token");
      } catch (_) { /* ignore */ }
      return { ...state, session: { ...state.session, logged_in_staff_id: null,
                                    staff_snapshot: null, locked: false, locked_at: null,
                                    session_error: null,
                                    current_till: null,
                                    last_till_summary: null, till_prompt_dismissed: false } };
    }

    // ── Sprint 2 B.2 · Till lifecycle ────────────────────────────────
    // These flip in response to /api/pos/tills/* calls made from
    // posLiveActions. TILL_HYDRATED runs on POS mount so a browser
    // reload re-attaches the caller to their existing open till.
    case ACT.TILL_OPENED:
    case ACT.TILL_HYDRATED: {
      const till = action.payload?.till || null;
      if (!till) return state;
      return {
        ...state,
        session: {
          ...state.session,
          current_till:         till,
          till_prompt_dismissed: true,
          session_error:        null,
        },
      };
    }
    case ACT.TILL_CLOSED: {
      // Payload carries the summary for THIS cashier alone — never
      // aggregated across cashiers. Stash it on last_till_summary so
      // the Z-slip UI can render THAT staff member's figures.
      const summary = action.payload?.summary || null;
      return {
        ...state,
        session: {
          ...state.session,
          current_till:          null,
          last_till_summary:     summary,
          till_prompt_dismissed: false,
        },
      };
    }
    case ACT.TILL_PROMPT_DISMISSED: {
      // Payload allows toggling the guard back off ({dismissed:false})
      // so the "Open till" button on the floor can re-surface the
      // float prompt without a lock/unlock cycle.
      const nextDismissed = action.payload?.dismissed === false ? false : true;
      return { ...state, session: { ...state.session, till_prompt_dismissed: nextDismissed } };
    }
    case ACT.TILL_WARN_HOURS_SET: {
      const hours = Number(action.payload?.hours);
      if (!Number.isFinite(hours) || hours <= 0) return state;
      return { ...state, session: { ...state.session, till_open_warning_hours: hours } };
    }

    case ACT.ORDER_DRAFT_RELEASED: {
      // POS Cleanup Pass 1 · confirmed server release. The endpoint
      // guaranteed no tickets/invoice/tenders exist, so we simply
      // drop the slice from state, roll back any takeaway token this
      // draft owned, and clear any dirty_tables grace entry. If it
      // was the active order, the active slot returns to null so the
      // billing screen navigates back to Floor.
      const p = action.payload || {};
      const target = p.order_id || state.active_order_id;
      if (!target || !state.orders[target]) return state;
      const nextOrders = { ...state.orders };
      delete nextOrders[target];
      const token = (state.session.tokens || []).find((t) => t.order_id === target);
      let tokens = state.session.tokens || [];
      let token_seq = state.session.token_seq || 0;
      if (token) {
        tokens = tokens.filter((t) => t.order_id !== target);
        if (token.number === token_seq) token_seq -= 1;
      }
      const dirty_tables = (state.session.dirty_tables || [])
        .filter((d) => d.order_id !== target);
      return {
        ...state,
        active_order_id: state.active_order_id === target ? null : state.active_order_id,
        orders:  nextOrders,
        session: { ...state.session, tokens, token_seq, dirty_tables },
      };
    }

    case ACT.NAVIGATE_TO_FLOOR: {
      // POS Cleanup Pass 1 · this reducer no longer silently deletes
      // an empty backend order. The caller (POSTerminal.goToFloor,
      // BillingScreen "Back") is now responsible for calling
      // `livePos.releaseDraft(order.id, "EMPTY_BACK")` FIRST and
      // dispatching ORDER_DRAFT_RELEASED on success. NAVIGATE_TO_FLOOR
      // only clears the active-order slot — the actual slice removal
      // happens in ORDER_DRAFT_RELEASED after the server confirms.
      return { ...state, active_order_id: null };
    }

    case ACT.ORDER_ACTIVATED: {
      const { order_id } = action.payload || {};
      if (!order_id || !state.orders[order_id]) return state;
      return { ...state, active_order_id: order_id };
    }

    case ACT.ORDER_OPENED: {
      // Creates a fresh non-dine-in order and activates it. Payload
      // may specify type, token, guest fields. Session preserved.
      // Part 6 · refuse if no business day is open.
      if (!state.session.business_date) return state;
      const p = action.payload || {};
      const type = p.type || ORDER_TYPE.TAKEAWAY;
      const id = p.id || uid("ord");
      // POS Cleanup Pass 1 · when a server id is supplied (live path),
      // the token value on the payload is authoritative. Do NOT mint
      // a local T-xxx sequence; if the server returned `token: null`
      // this takeaway simply has no token badge until the server
      // assigns one.
      const isLiveOpen = !!p.id;
      let nextTokenSeq = state.session.token_seq || 0;
      let nextTokens = state.session.tokens || [];
      let assignedToken = p.token || null;
      if (!isLiveOpen && type === ORDER_TYPE.TAKEAWAY) {
        nextTokenSeq += 1;
        assignedToken = `T-${String(nextTokenSeq).padStart(3, "0")}`;
        nextTokens = [...nextTokens, {
          number: nextTokenSeq, label: assignedToken,
          order_id: id, state: "preparing",
          // Sprint 5 correction · token cards need enough context to
          // hand the right order to the right guest. Snapshot the
          // fields at token allocation time so a subsequent name edit
          // on the order doesn't retroactively change the printed
          // token.
          order_type: "TAKEAWAY",
          guest_name:  p.guest_name  || null,
          allocated_at: Date.now(),
        }];
      } else if (!isLiveOpen && !assignedToken && type !== ORDER_TYPE.DINE_IN) {
        assignedToken = `T-${Math.floor(Math.random() * 900) + 100}`;
      }
      const new_slice = {
        order: {
          id,
          state: ORDER_STATE.OPEN,
          type,
          table_id: p.table_id || null,
          token: assignedToken,
          covers: p.covers ?? (type === ORDER_TYPE.DINE_IN ? 2 : null),
          steward_id: p.steward_id || getActor(state).id,
          guest_name:  p.guest_name  || null,
          guest_phone: p.guest_phone || null,
          guest_address: p.guest_address || null,          // Part 5.2 delivery
          guest_consent_at: p.consent_captured_at || null, // Part 5.3
          opened_at: Date.now(),
          invoice_no: null,
          void_reason: null, void_authorised_by: null, void_at: null,
        },
        lines: [],
        payments: [],
      };
      return {
        ...state,
        orders: { ...state.orders, [id]: new_slice },
        active_order_id: id,
        session: {
          ...state.session,
          token_seq: nextTokenSeq,
          tokens: nextTokens,
        },
      };
    }

    case ACT.AGGREGATOR_ACCEPTED: {
      // Part 6 · imports a mocked aggregator payload into a fresh
      // order and fires its lines to the appropriate destinations
      // (kitchen / bar based on ITEMS routing). Settlement mode is
      // pre-set to the originating platform and the aggregator
      // reference is stored on the order. No network calls.
      // Sprint 3 · refuse if no business day is open.
      if (!state.session.business_date) return state;
      const p = action.payload || {};
      const agg = p.aggregator || {};
      if (!agg.id) return state;
      if (state.session.aggregator_processed?.[agg.id]) return state;

      const id = uid("ord");
      const now = Date.now();
      let kseq = state.session.ticket_seq?.kitchen || 0;
      let bseq = state.session.ticket_seq?.bar     || 0;
      // Map aggregator items to POS lines, allocating ticket ids
      // per destination immediately (accepted orders arrive already
      // committed by the guest).
      const destSeq = {};
      const lines = (agg.items || []).map((it) => {
        const menuItem = itemById(it.item_id) || {};
        const dest = menuItem.routing || "kitchen";
        if (!destSeq[dest]) {
          destSeq[dest] = dest === "bar" ? ++bseq : ++kseq;
        }
        const label = (dest === "bar" ? "BOT_" : "KOT_") + destSeq[dest];
        return {
          id: uid("ln"),
          item_id: it.item_id,
          item_name: menuItem.name || it.item_id,
          qty: Number(it.qty) || 1,
          unit_price: Number(menuItem.price || 0),
          routing: dest,
          note: "",
          discount: 0,
          ticket_id: label,
          ticket_dest: dest,
          ticket_fired_at: now,
        };
      });
      const platform = agg.platform === "zomato" ? "zomato" : "swiggy_dineout";

      // Sprint 5 correction · aggregator orders also enter the token
      // queue so the counter staff can hand the right bag to the
      // right rider. The token carries the platform label + platform
      // ref so the token card can render Swiggy / Zomato distinctly.
      let nextTokenSeq = state.session.token_seq || 0;
      let nextTokens = state.session.tokens || [];
      nextTokenSeq += 1;
      const tokenLabel = `T-${String(nextTokenSeq).padStart(3, "0")}`;
      nextTokens = [...nextTokens, {
        number: nextTokenSeq, label: tokenLabel,
        order_id: id, state: "preparing",
        order_type: agg.platform === "zomato" ? "ZOMATO" : "SWIGGY",
        aggregator_ref: agg.platform_ref || null,
        guest_name: agg.guest?.name || null,
        allocated_at: now,
      }];

      const new_slice = {
        order: {
          id,
          state: ORDER_STATE.OPEN,
          type: ORDER_TYPE.DELIVERY,
          table_id: null,
          token: agg.platform_ref || null,
          covers: null,
          steward_id: getActor(state).id,
          guest_name:  agg.guest?.name  || null,
          guest_phone: agg.guest?.phone || null,
          guest_address: agg.guest?.address || null,
          guest_consent_at: now,
          opened_at: now,
          invoice_no: null,
          void_reason: null, void_authorised_by: null, void_at: null,
          // Part 6 · aggregator provenance
          source: "aggregator",
          aggregator_platform: agg.platform,
          aggregator_ref:      agg.platform_ref,
          preset_payment_mode: platform,
        },
        lines,
        payments: [],
      };
      return {
        ...state,
        orders: { ...state.orders, [id]: new_slice },
        active_order_id: id,
        session: {
          ...state.session,
          ticket_seq: { kitchen: kseq, bar: bseq },
          token_seq: nextTokenSeq,
          tokens: nextTokens,
          aggregator_processed: {
            ...(state.session.aggregator_processed || {}),
            [agg.id]: { action: "accepted", order_id: id, at: now },
          },
        },
      };
    }

    case ACT.AGGREGATOR_REJECTED: {
      // Part 6 · rejection requires a reason_code. Drops the item from
      // the tray via session.aggregator_processed.
      const p = action.payload || {};
      if (!p.aggregator_id || !p.reason_code) return state;
      return {
        ...state,
        session: {
          ...state.session,
          aggregator_processed: {
            ...(state.session.aggregator_processed || {}),
            [p.aggregator_id]: {
              action: "rejected",
              reason_code:   p.reason_code,
              note:          p.note || "",
              authorised_by: p.authorised_by || getActor(state).name,
              at: Date.now(),
            },
          },
        },
      };
    }

    case ACT.TABLE_OPENED: {
      // Task 1b — this is the compound event dispatched from the
      // table-open dialog. Per spec it emits three logical events in
      // order: ORDER_OPENED, TABLE_ASSIGNED, COVERS_SET. We handle
      // all three atomically here so no partial phantom order can
      // ever land on the floor.
      // Part 6 · refuse if no business day is open.
      if (!state.session.business_date) return state;
      const p = action.payload || {};
      if (!p.table_id) return state;
      const id = p.id || uid("ord");
      const new_slice = {
        order: {
          id,
          state: ORDER_STATE.OPEN,
          type: ORDER_TYPE.DINE_IN,
          table_id: p.table_id,
          token: null,
          covers: Number(p.covers) || 1,
          steward_id: p.steward_id || getActor(state).id,
          guest_name:  p.guest_name  || null,
          guest_phone: p.guest_phone || null,
          guest_address: p.guest_address || null,          // Part 5.2 delivery
          guest_consent_at: p.consent_captured_at || null, // Part 5.3
          opened_at: Date.now(),
          invoice_no: null,
          void_reason: null, void_authorised_by: null, void_at: null,
        },
        lines: [], payments: [],
      };
      return {
        ...state,
        orders: { ...state.orders, [id]: new_slice },
        active_order_id: id,
        // If the table was dirty, opening a fresh order implies it
        // was cleaned. Remove the dirty flag.
        session: {
          ...state.session,
          dirty_tables: state.session.dirty_tables.filter((d) => d.table_id !== p.table_id),
        },
      };
    }

    case ACT.TABLE_MARKED_DIRTY: {
      const p = action.payload || {};
      if (!p.table_id) return state;
      // Idempotent — don't stack duplicate dirty entries.
      if (state.session.dirty_tables.some((d) => d.table_id === p.table_id)) return state;
      return {
        ...state,
        session: {
          ...state.session,
          dirty_tables: [...state.session.dirty_tables, { table_id: p.table_id, at: Date.now() }],
        },
      };
    }

    case ACT.TABLE_CLEARED: {
      const { table_id } = action.payload || {};
      if (!table_id) return state;
      return {
        ...state,
        session: {
          ...state.session,
          dirty_tables: state.session.dirty_tables.filter((d) => d.table_id !== table_id),
        },
      };
    }

    case ACT.TABLE_MARKED_UNAVAILABLE: {
      // Part 3+5 · reason_code + optional note. Idempotent by table_id.
      const p = action.payload || {};
      if (!p.table_id) return state;
      const filtered = (state.session.unavailable_tables || []).filter((u) => u.table_id !== p.table_id);
      return {
        ...state,
        session: {
          ...state.session,
          unavailable_tables: [...filtered, {
            table_id: p.table_id,
            reason_code: p.reason_code || "OTHER",
            note: p.note || "",
            at: Date.now(),
          }],
        },
      };
    }

    case ACT.TEMP_TABLE_CREATED: {
      // Part 9 · ephemeral floor entry. Payload { label? }.
      const p = action.payload || {};
      const id = `t_temp_${Math.random().toString(36).slice(2, 8)}`;
      const label = (p.label && p.label.trim()) || `Temp ${(state.session.temp_tables || []).length + 1}`;
      return {
        ...state,
        session: {
          ...state.session,
          temp_tables: [...(state.session.temp_tables || []), { id, label, created_at: Date.now() }],
        },
      };
    }

    case ACT.TEMP_TABLE_REMOVED: {
      const { table_id } = action.payload || {};
      if (!table_id) return state;
      return {
        ...state,
        session: {
          ...state.session,
          temp_tables: (state.session.temp_tables || []).filter((t) => t.id !== table_id),
        },
      };
    }

    case ACT.OPEN_ITEM_TOGGLED: {
      return {
        ...state,
        session: { ...state.session, open_item_enabled: !state.session.open_item_enabled },
      };
    }

    case ACT.OPEN_ITEM_CREATED: {
      // Sprint 4 correction · Manager PIN gate is enforced at the
      // call site (SharedPinDialog). Reducer just records the entry.
      // Entries live on session.managed_open_items and clear on the
      // next BUSINESS_DAY_OPENED / BUSINESS_DAY_CLOSED so they never
      // leak across trading days.
      if (!state.session.business_date) return state;
      const p = action.payload || {};
      const name = (p.name || "").trim();
      const price = Number(p.price) || 0;
      if (!name || price <= 0) return state;
      const cat = ["food", "beverage", "alcohol", "other"].includes(p.category) ? p.category : "food";
      const entry = {
        id: `open_${Math.random().toString(36).slice(2, 8)}`,
        name, price, category: cat,
        created_by: p.authorised_by || getActor(state).name,
        created_at: Date.now(),
      };
      return {
        ...state,
        session: {
          ...state.session,
          managed_open_items: [...(state.session.managed_open_items || []), entry],
        },
      };
    }

    case ACT.TABLE_MADE_AVAILABLE: {
      const { table_id } = action.payload || {};
      if (!table_id) return state;
      return {
        ...state,
        session: {
          ...state.session,
          unavailable_tables: (state.session.unavailable_tables || []).filter((u) => u.table_id !== table_id),
        },
      };
    }

    case ACT.TABLE_TRANSFERRED: {
      // Part 7 · move an order's table_id to a new table. Fails
      // silently if the destination already has an active order
      // (Contract §9-7 UNRESOLVED — we refuse rather than merge).
      const { order_id, to_table_id } = action.payload || {};
      if (!order_id || !to_table_id) return state;
      const src = state.orders[order_id];
      if (!src) return state;
      const collision = Object.values(state.orders).some((o) =>
        o.order.table_id === to_table_id
        && o.order.state !== ORDER_STATE.SETTLED
        && o.order.state !== ORDER_STATE.VOIDED
      );
      if (collision) return state;
      return {
        ...state,
        orders: {
          ...state.orders,
          [order_id]: { ...src, order: { ...src.order, table_id: to_table_id } },
        },
      };
    }

    case ACT.ITEM_TRANSFERRED: {
      // Part 7 · move a single line from source order to destination
      // order. Fired lines carry their ticket_id — the ticket record on
      // the source is unchanged (Contract §4: append-only).
      const { source_order_id, line_id, dest_order_id } = action.payload || {};
      if (!source_order_id || !line_id || !dest_order_id) return state;
      const src = state.orders[source_order_id];
      const dst = state.orders[dest_order_id];
      if (!src || !dst) return state;
      const line = src.lines.find((l) => l.id === line_id);
      if (!line) return state;
      const clone = { ...line, id: uid("ln"), transferred_from: source_order_id };
      return {
        ...state,
        orders: {
          ...state.orders,
          [source_order_id]: { ...src, lines: src.lines.filter((l) => l.id !== line_id) },
          [dest_order_id]:   { ...dst, lines: [...dst.lines, clone] },
        },
      };
    }

    case ACT.ORDER_SPLIT: {
      // Part 6 · creates N children from parent. Payload:
      //   { source_order_id, groups: [{ line_ids: [...], label }] }
      // Parent lines get consumed and become split_children[]. Parent
      // stays OPEN (until BILL_PRINTED). Contract §9-6 UNRESOLVED —
      // prototype assumes ONE-ORDER-MANY-CHILDREN semantic.
      const { source_order_id, groups } = action.payload || {};
      if (!source_order_id || !Array.isArray(groups) || groups.length === 0) return state;
      const src = state.orders[source_order_id];
      if (!src) return state;
      // Guard: split allowed only while OPEN (Contract §3). Post-print
      // splits go through INVOICE_VOIDED first.
      if (src.order.state !== ORDER_STATE.OPEN) return state;

      const newOrders = { ...state.orders };
      const children_ids = [];
      groups.forEach((g, idx) => {
        const childId = uid("ord");
        const childLines = (g.line_ids || []).map((lid) => src.lines.find((l) => l.id === lid)).filter(Boolean).map((l) => ({ ...l, id: uid("ln"), split_from: source_order_id }));
        newOrders[childId] = {
          order: {
            id: childId,
            state: ORDER_STATE.OPEN,
            type: src.order.type,
            table_id: src.order.table_id,
            token: null,
            covers: src.order.covers,
            steward_id: src.order.steward_id,
            guest_name: null, guest_phone: null,
            opened_at: Date.now(),
            invoice_no: null,
            void_reason: null, void_authorised_by: null, void_at: null,
            split_from: source_order_id,
            split_label: g.label || `Group ${idx + 1}`,
            parent_voided_invoice: src.order.voided_invoice || null, // Part 6.3
          },
          lines: childLines,
          payments: [],
        };
        children_ids.push(childId);
      });
      // Retained lines on parent: everything NOT in any group.
      const consumed = new Set(groups.flatMap((g) => g.line_ids || []));
      const retainedLines = src.lines.filter((l) => !consumed.has(l.id));
      newOrders[source_order_id] = {
        ...src,
        lines: retainedLines,
        order: { ...src.order, split_children: [...(src.order.split_children || []), ...children_ids] },
      };
      return { ...state, orders: newOrders };
    }

    case ACT.BILL_REPRINTED: {
      // POS Control Sprint · Manager-authorised reprints record on
      // `session.reprints` so the Exception Report has a per-invoice
      // history. The audited event carries reason_code, notes and the
      // authorising manager (payload from the SharedPinDialog).
      const active = getActive(state);
      if (!active?.order?.invoice_no) return state;
      const p = action.payload || null;
      const entry = {
        invoice_no:    active.order.invoice_no,
        order_id:      active.order.id,
        count:         (active.order.reprint_count || 0) + 1,
        reprinted_by:  getActor(state).name,
        at:            Date.now(),
        reason_code:   p?.reason_code || null,
        notes:         p?.notes || null,
        authorised_by: p?.authorised_by || null,
      };
      const reprints = [...(state.session.reprints || []), entry];
      // Bump the counter/timestamp on the underlying order via the
      // slice reducer so the receipt watermark shows the right count.
      const newOrders = { ...state.orders };
      newOrders[active.order.id] = orderReducer(active, action);
      return { ...state, orders: newOrders, session: { ...state.session, reprints } };
    }

    case ACT.INVOICE_VOIDED: {
      // Part 6 · Voids a specific invoice number (records to
      // session.invoice_voids so every allocated number resolves to
      // something — Contract §3) and reopens the order to OPEN so a
      // follow-up ORDER_SPLIT or additional lines are allowed.
      // Distinct from ORDER_VOIDED, which terminates the whole order.
      // Sprint 4 · authorisation is now enforced by SharedPinDialog at
      // the call site (single source of truth). The reducer trusts
      // `authorised_by` on the payload; the legacy `split_manager_pin`
      // parameter is no longer consulted.
      const { source_order_id, authorised_by, reason_code, note } = action.payload || {};
      if (!source_order_id) return state;
      const src = state.orders[source_order_id];
      if (!src) return state;
      if (src.order.state !== ORDER_STATE.PRINTED) return state;
      const voidedInvoice = src.order.invoice_no || null;
      const voids = [...(state.session.invoice_voids || []), {
        source_order_id, voided_invoice: voidedInvoice,
        authorised_by: authorised_by || getActor(state).name,
        reason_code:   reason_code   || "POST_PRINT_SPLIT",
        note:          note          || "",
        value:         computeTotalsFor(state, source_order_id).grand_total,
        at: Date.now(),
      }];
      return {
        ...state,
        session: { ...state.session, invoice_voids: voids },
        orders: {
          ...state.orders,
          [source_order_id]: {
            ...src,
            order: {
              ...src.order,
              state: ORDER_STATE.OPEN,     // reopen so more items / split are allowed
              invoice_no: null,             // invoice number consumed as void
              voided_invoice: voidedInvoice,
              invoice_voided_by: authorised_by || getActor(state).name,
            },
          },
        },
      };
    }

    case ACT.BUSINESS_DAY_OPENED: {
      // Part 6 · Explicit service period. Idempotent: opening while
      // already open is a no-op (previous business_date carries).
      if (state.session.business_date) return state;
      const p = action.payload || {};
      const now = Date.now();
      const d = new Date(now);
      const yyyymmdd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const chosen = p.business_date || yyyymmdd;
      // Sprint 4 correction 6 · a closed day cannot be reopened by
      // date selection alone. The chosen date must be strictly after
      // the last closed day unless an explicit reopen override has
      // been provided (PIN authorisation captured by the caller).
      const last_closed = state.session.last_closed_business_date;
      const isReopen = !!(last_closed && chosen <= last_closed);
      if (isReopen && !p.reopen_override) return state;
      // Track reopen overrides in an audit log surfaced by the
      // exception report.
      const reopens = state.session.business_day_reopens || [];
      const nextReopens = isReopen
        ? [...reopens, {
            id: uid("dayrep"),
            business_date: chosen,
            last_closed_before: last_closed,
            authorised_by: p.reopen_override?.authorised_by || getActor(state).name,
            reason_code:   p.reopen_override?.reason_code   || "REOPEN_CLOSED_DAY",
            note:          p.reopen_override?.note          || null,
            at:            now,
          }]
        : reopens;
      return {
        ...state,
        session: {
          ...state.session,
          business_date:          chosen,
          business_day_opened_at: now,
          business_day_opened_by: p.opened_by || getActor(state).name,
          business_day_closed_at: null,
          business_day_closed_by: null,
          // Reset per-day counters so a fresh day starts numbering clean.
          ticket_seq: { kitchen: 0, bar: 0 },
          invoice_seq: 0,
          token_seq: 0,
          tokens: [],
          // Sprint 4 correction · managed open items are strictly
          // business-day scoped. Prior day's ad-hoc pricing must never
          // survive into a new trading day.
          managed_open_items: [],
          business_day_reopens: nextReopens,
        },
      };
    }

    case ACT.BUSINESS_DAY_CLOSED: {
      // POS Lifecycle Final Fix · The reducer NO LONGER manufactures
      // the next business day. Server owns the transition:
      //   1. Backend `close_business_day` closes the current day and
      //      returns the outlet-timezone-aware `next_business_date`.
      //   2. FloorView's Close Day orchestrator calls
      //      `openBusinessDay(next_business_date)` immediately after,
      //      which re-hydrates from session-status → the reducer
      //      applies BUSINESS_DAY_OPENED with the SERVER-confirmed
      //      date. This case only clears the day here — never
      //      auto-rolls locally.
      if (!state.session.business_date) return state;
      const p = action.payload || {};
      const unsettled = Object.values(state.orders).filter((o) =>
        o.order.state !== ORDER_STATE.SETTLED &&
        o.order.state !== ORDER_STATE.VOIDED &&
        (o.lines || []).length > 0 &&
        !isEmptySplitParent(o)
      );
      const openShifts = (state.session.shifts || []).filter((s) => !s.closed_at);
      if (unsettled.length > 0 || openShifts.length > 0) return state;
      const now = Date.now();
      const actor = getActor(state);
      const prev_business_date = state.session.business_date;
      return {
        ...state,
        session: {
          ...state.session,
          business_day_closed_at:    now,
          business_day_closed_by:    p.closed_by || actor.name || "system",
          last_closed_business_date: prev_business_date,
          managed_open_items:        [],
          tokens:                    [],
          token_seq:                 0,
          // No auto-roll. Terminal now shows the "Business day
          // required" state; the Close Day orchestrator will call
          // openBusinessDay(server_next_date) which rehydrates the
          // session with the persisted next day.
          business_date:          null,
          business_day_opened_at: null,
          business_day_opened_by: null,
          business_day_id:        null,
          ticket_seq:  { kitchen: 0, bar: 0 },
          invoice_seq: 0,
        },
      };
    }

    case ACT.SHIFT_OPENED: {
      // POS Cleanup Pass 2 · Float belongs ONLY to the Till entity.
      // Shift open no longer carries opening_float. Any legacy payload
      // is ignored; the shift record keeps opening_float=0 for
      // schema compatibility (server also refuses opening_float on
      // shift open — see backend/routes/pos_orders.py ShiftOpenIn).
      if (!state.session.business_date) return state;
      const p = action.payload || {};
      const shift = {
        id: uid("shift"),
        opening_float: 0,
        opened_at: Date.now(),
        opened_by: p.opened_by || getActor(state).name,
        closed_at: null,
        closed_by: null,
        blind:     !!p.blind,
        counted_cash: null,
        expected_cash: null,
        variance: null,
        reason_code: null,
        note: null,
        denominations: null,
        expected_by_mode: null,
      };
      return {
        ...state,
        session: {
          ...state.session,
          shifts: [...(state.session.shifts || []), shift],
          session_error: null,      // shift now open — clear any block
        },
      };
    }

    case ACT.SHIFT_CLOSED: {
      // Part 7 · Closes the specified shift. Payload carries the
      // count, denominations, expected snapshot per settlement mode,
      // and reason if variance exceeds threshold.
      const p = action.payload || {};
      if (!p.shift_id) return state;
      const shifts = state.session.shifts || [];
      const idx = shifts.findIndex((s) => s.id === p.shift_id);
      if (idx < 0 || shifts[idx].closed_at) return state;
      const updated = {
        ...shifts[idx],
        closed_at:        Date.now(),
        closed_by:        p.closed_by || getActor(state).name,
        counted_cash:     Number(p.counted_cash) || 0,
        expected_cash:    Number(p.expected_cash) || 0,
        variance:         Number(p.counted_cash || 0) - Number(p.expected_cash || 0),
        reason_code:      p.reason_code || null,
        note:             p.note || null,
        denominations:    p.denominations || null,
        expected_by_mode: p.expected_by_mode || null,
        // Sprint 4 correction · EDC batch (card + UPI) reconciled
        // independently. Each variance carries its own threshold
        // check and its own reason; never netted against cash.
        edc_batch_total:  Number(p.edc_batch_total) || 0,
        edc_expected:     Number(p.edc_expected)    || 0,
        edc_variance:     Number(p.edc_variance)    || 0,
        edc_reason_code:  p.edc_reason_code || null,
        edc_note:         p.edc_note || null,
        combined_variance: Number(p.combined_variance) || 0,
      };
      const nextShifts = [...shifts.slice(0, idx), updated, ...shifts.slice(idx + 1)];
      // Closing a shift returns the terminal to the PIN screen and
      // clears the token — a shared device must never let a fresh
      // sign-in inherit the previous session.
      try {
        if (typeof window !== "undefined") window.localStorage.removeItem("pos_token");
      } catch (_) { /* ignore */ }
      return {
        ...state,
        session: {
          ...state.session,
          shifts:             nextShifts,
          logged_in_staff_id: null,
          staff_snapshot:     null,
          locked:             false,
          locked_at:          null,
          session_error:      null,
        },
      };
    }

    case ACT.X_REPORT_TAKEN: {
      // Part 8 · non-destructive read. Repeatable. Snapshot only.
      const p = action.payload || {};
      const snap = p.snapshot || null;
      const entry = {
        id: uid("xrep"),
        taken_at: Date.now(),
        taken_by: p.taken_by || getActor(state).name,
        shift_id: p.shift_id || null,
        snapshot: snap,
      };
      return {
        ...state,
        session: {
          ...state.session,
          x_reports: [...(state.session.x_reports || []), entry],
        },
      };
    }

    case ACT.Z_REPORT_GENERATED: {
      // Part 8 · immutable, sequentially numbered per outlet.
      const p = action.payload || {};
      if (!p.shift_id) return state;
      // No double generation for the same shift.
      if ((state.session.z_reports || []).some((z) => z.shift_id === p.shift_id)) return state;
      const next_seq = (state.session.z_report_seq || 0) + 1;
      const entry = {
        id: uid("zrep"),
        z_number:   `${state.session.outlet_code}/${state.session.fy}/Z${String(next_seq).padStart(4, "0")}`,
        generated_at: Date.now(),
        generated_by: p.generated_by || getActor(state).name,
        shift_id: p.shift_id,
        business_date: state.session.business_date,
        snapshot: p.snapshot || null,
      };
      return {
        ...state,
        session: {
          ...state.session,
          z_reports:   [...(state.session.z_reports || []), entry],
          z_report_seq: next_seq,
        },
      };
    }

    case ACT.TICKET_MARKED_READY: {
      // Part 11 · KDS mark ready. Attaches a ready_at to every line
      // sharing the ticket_id. Also promotes the matching token from
      // preparing → ready if a takeaway token exists.
      const { ticket_id } = action.payload || {};
      if (!ticket_id) return state;
      const now = Date.now();
      const nextOrders = {};
      let promotedOrderId = null;
      for (const [oid, slice] of Object.entries(state.orders)) {
        let mutated = false;
        const nextLines = slice.lines.map((l) => {
          if (l.ticket_id === ticket_id && !l.ticket_ready_at) {
            mutated = true;
            return { ...l, ticket_ready_at: now };
          }
          return l;
        });
        if (mutated) {
          // If ALL fired lines on this order are ready, mark the token ready.
          const stillFrying = nextLines.some((l) => l.ticket_id && !l.ticket_ready_at);
          // Sprint 5 correction · aggregator orders also carry tokens
          // now, so a fully-bumped aggregator ticket must promote its
          // token to Ready alongside takeaways.
          const hasToken = slice.order.type === ORDER_TYPE.TAKEAWAY
                        || slice.order.source === "aggregator";
          if (!stillFrying && hasToken) {
            promotedOrderId = slice.order.id;
          }
          nextOrders[oid] = { ...slice, lines: nextLines };
        } else {
          nextOrders[oid] = slice;
        }
      }
      let nextTokens = state.session.tokens || [];
      if (promotedOrderId) {
        nextTokens = nextTokens.map((t) =>
          t.order_id === promotedOrderId && t.state === "preparing"
            ? { ...t, state: "ready", ready_at: now }
            : t
        );
      }
      return { ...state, orders: nextOrders, session: { ...state.session, tokens: nextTokens } };
    }

    case ACT.TOKEN_MARKED_READY: {
      // Sprint 5 correction · manual promotion path (tapping a
      // Preparing token). The KDS bump path (TICKET_MARKED_READY)
      // continues to promote automatically when the last kitchen /
      // bar ticket on the order is bumped. Either route is valid.
      const { number } = action.payload || {};
      if (number == null) return state;
      const now = Date.now();
      const tokens = (state.session.tokens || []).map((t) =>
        t.number === number && t.state === "preparing"
          ? { ...t, state: "ready", ready_at: now }
          : t
      );
      return { ...state, session: { ...state.session, tokens } };
    }

    case ACT.TOKEN_MARKED_COLLECTED: {
      // Part 12 · Removes the token from the queue by flipping state.
      const { number } = action.payload || {};
      if (number == null) return state;
      const now = Date.now();
      const tokens = (state.session.tokens || []).map((t) =>
        t.number === number && t.state === "ready"
          ? { ...t, state: "collected", collected_at: now }
          : t
      );
      return { ...state, session: { ...state.session, tokens } };
    }

    case ACT.SLIP_ACKNOWLEDGED: {
      // Part 10 · dismiss the currently-showing slip (head of queue).
      const q = state.session.slip_queue || [];
      if (q.length === 0) return state;
      return { ...state, session: { ...state.session, slip_queue: q.slice(1) } };
    }

    case ACT.TERMINAL_CLEARED: {
      // POS Cleanup Pass 1 · post-settle behaviour is now uniform:
      // clear the active-order slot and return to the start/floor
      // state. A new takeaway/delivery/room-service order must be
      // opened via a real `livePos.openOrder()` call — this reducer
      // never mints a `uid("ord")` order any more.
      const patch = action.payload || {};
      let next_session = state.session;
      if (patch.temp_table_id) {
        next_session = {
          ...next_session,
          temp_tables: (next_session.temp_tables || []).filter((t) => t.id !== patch.temp_table_id),
          dirty_tables: (next_session.dirty_tables || []).filter((d) => d.table_id !== patch.temp_table_id),
        };
      }
      return { ...state, session: next_session, active_order_id: null };
    }

    default:
      break;
  }

  // Every other action targets the ACTIVE order slice. If nothing is
  // active we can't route the action.
  const active_id = state.active_order_id;
  if (!active_id || !state.orders[active_id]) return state;
  const slice = state.orders[active_id];
  const next_slice_raw = orderReducer(slice, action, state.session);
  if (next_slice_raw === slice) return state;

  // Extract any pending session mutations the sub-reducer flagged.
  const next_slice = { ...next_slice_raw };
  const pending_cancel = next_slice._pending_cancellation;
  const pending_ticket = next_slice._pending_ticket_seq;
  const pending_inv    = next_slice._pending_invoice_seq;
  const pending_link   = next_slice._pending_linkage;
  const pending_settled_table = next_slice._pending_settled_table;
  const pending_slips = next_slice._pending_slips;
  delete next_slice._pending_cancellation;
  delete next_slice._pending_ticket_seq;
  delete next_slice._pending_invoice_seq;
  delete next_slice._pending_linkage;
  delete next_slice._pending_settled_table;
  delete next_slice._pending_slips;

  let next_session = state.session;
  if (pending_cancel) {
    next_session = { ...next_session, ticket_cancellations: [...(next_session.ticket_cancellations || []), pending_cancel] };
    // Part 10 (Sprint 3) · a cancellation slip prints on TICKET_ITEM_CANCELLED.
    const cancelSlip = {
      id: `${pending_cancel.ticket_id}-CX-${Date.now()}`,
      kind: "cancel",
      ticket_id: pending_cancel.ticket_id,
      dest: pending_cancel.ticket_dest,
      at: pending_cancel.at,
      type: slice.order.type,
      covers: slice.order.covers,
      table_label: tableLabelFor(slice.order.table_id),
      token: slice.order.token,
      steward_id: slice.order.steward_id,
      authorised_by: pending_cancel.authorised_by,
      reason_code:  pending_cancel.reason_code,
      lines: [{ item_name: pending_cancel.item_name, qty: pending_cancel.qty_cancelled, note: pending_cancel.note }],
    };
    next_session = { ...next_session, slip_queue: [...(next_session.slip_queue || []), cancelSlip] };
  }
  if (pending_slips && pending_slips.length > 0) {
    next_session = { ...next_session, slip_queue: [...(next_session.slip_queue || []), ...pending_slips] };
  }
  if (pending_ticket) {
    next_session = { ...next_session, ticket_seq: pending_ticket };
  }
  if (pending_inv != null) {
    next_session = { ...next_session, invoice_seq: pending_inv };
  }
  if (pending_link) {
    const replacements = { ...(next_session.invoice_replacements || {}) };
    // parent → children[]
    const kids = replacements[pending_link.replaced] || [];
    if (!kids.includes(pending_link.child)) kids.push(pending_link.child);
    replacements[pending_link.replaced] = kids;
    // child → parent
    replacements[pending_link.child] = pending_link.replaced;
    next_session = { ...next_session, invoice_replacements: replacements };
  }
  if (pending_settled_table) {
    // Part 2 · a dine-in order just SETTLED. Push dirty_tables ONLY
    // if no sibling / child order remains non-terminal on this table.
    const nextOrdersPreview = { ...state.orders, [active_id]: next_slice };
    const stillActive = Object.values(nextOrdersPreview).some((o) =>
      o.order.id !== active_id &&
      o.order.table_id === pending_settled_table &&
      o.order.state !== ORDER_STATE.SETTLED &&
      o.order.state !== ORDER_STATE.VOIDED &&
      !isEmptySplitParent(o)
    );
    if (!stillActive) {
      const dt = next_session.dirty_tables || [];
      if (!dt.some((d) => d.table_id === pending_settled_table)) {
        next_session = { ...next_session, dirty_tables: [...dt, { table_id: pending_settled_table, at: Date.now() }] };
      }
    }
  }

  return {
    ...state,
    session: next_session,
    orders: { ...state.orders, [active_id]: next_slice },
  };
}

// ─── Part 2 · Tax regression fixture ────────────────────────────────
// Hardcoded mixed food + liquor order — a scenario that exercises
// EVERY tax slab and the round-off rule. Kept next to computeTotals so
// any accidental drift on either helper is caught by a single console
// assertion at boot. Numbers were verified by hand:
//
//   Paneer Tikka       320 × 2 =  640  (food, 5% GST)
//   Chicken Biryani    340 × 1 =  340  (food, 5% GST)
//   Kingfisher 650     340 × 2 =  680  (liquor, 20% VAT)
//   Whiskey 30ml       280 × 1 =  280  (liquor, 20% VAT)
//                      subtotal 1940
//   Food net           =  980   → CGST 2.5% =  24.50, SGST 2.5% =  24.50
//   Liquor net         =  960   → VAT   20% = 192.00
//   Raw total          = 1940 + 49.00 + 192.00 = 2181.00
//   Grand (Math.round) = 2181, round_off = 0.00
//
export const TAX_REGRESSION_FIXTURE = Object.freeze({
  name: "mixed_food_liquor_v1",
  lines: [
    { item_id: "i_paneer_tikka",   qty: 2, unit_price: 320 },
    { item_id: "i_chicken_biryani",qty: 1, unit_price: 340 },
    { item_id: "i_kingfisher",     qty: 2, unit_price: 340 },
    { item_id: "i_whiskey_peg",    qty: 1, unit_price: 280 },
  ],
  expected: {
    subtotal:    1940.00,
    // Sprint 6 correction 1 · under per-component rounding the two
    // half-rupee tax components round up to 25 each (24.50 → 25).
    cgst:          25,
    sgst:          25,
    vat:          192,
    tax_total:    242,
    grand_total:  2182,
    round_off:      0,
    has_food:    true,
    has_liquor:  true,
  },
});

// Sprint 6 correction 1 · Extended fixture — fractional tax on BOTH a
// GST line AND a VAT line to prove component-level rounding is
// independent (each rounds on its own; nothing cancels).
//
//   Item A · Chilli Paneer   qty 1 @ ₹347   → food, 5% GST
//     net    = 347.00
//     CGST 2.5% = 8.675 → rounds to 9
//     SGST 2.5% = 8.675 → rounds to 9
//   Item B · Whiskey 30ml    qty 1 @ ₹283   → liquor, 20% VAT
//     net    = 283.00
//     VAT 20% = 56.60   → rounds to 57
//
//   subtotal      = 630.00
//   tax (rounded) = 9 + 9 + 57 = 75
//   raw_total     = 630 + 75 = 705
//   grand_total   = 705 (already integer)
//   round_off     = 0
//
// Total-only mode retains paise on tax lines: CGST 8.675, SGST 8.675,
// VAT 56.60, raw_total = 630 + 73.95 = 703.95, grand_total = 704,
// round_off = 0.05.
export const TAX_REGRESSION_FIXTURE_FRACTIONAL = Object.freeze({
  name: "fractional_gst_and_vat_v1",
  lines: [
    { item_id: "i_chilli_paneer", qty: 1, unit_price: 347 },
    { item_id: "i_whiskey_peg",   qty: 1, unit_price: 283 },
  ],
  expected_per_component: {
    subtotal:    630.00,
    cgst:          9,
    sgst:          9,
    vat:          57,
    tax_total:    75,
    grand_total: 705,
    round_off:     0,
  },
  expected_total_only: {
    subtotal:    630.00,
    cgst:          8.675,
    sgst:          8.675,
    vat:          56.60,
    grand_total: 704,
    // 704 - (630 + 8.675 + 8.675 + 56.60) = 704 - 703.95 = 0.05
    round_off:     0.05,
  },
});

/**
 * Runs the tax fixture against computeTotals in an isolated state
 * shell. Returns { pass: bool, diffs: [...] }. Intended to be called
 * at boot for a console-visible sanity check — no build gate.
 */
export function verifyTaxFixture(fx = TAX_REGRESSION_FIXTURE, opts = {}) {
  const testState = {
    session: { outlet_code: "TEST", fy: "25-26", ticket_seq: { kitchen: 0, bar: 0 }, invoice_seq: 0, ticket_cancellations: [], dirty_tables: [], unavailable_tables: [] },
    orders: { test_order: {
      order: { id: "test_order", state: ORDER_STATE.OPEN, type: ORDER_TYPE.DINE_IN, table_id: null, covers: 2, steward_id: "s_1", opened_at: 0, invoice_no: null },
      lines: fx.lines.map((l, i) => ({
        id: `ln_${i}`, item_id: l.item_id, item_name: l.item_id, qty: l.qty,
        unit_price: l.unit_price, variant: null, addons: [], note: "", discount: 0, ticket_id: null,
      })),
      payments: [],
    }},
    active_order_id: "test_order",
    connection: { online: true },
  };
  const got = computeTotals(testState, opts);
  const expected = opts.expected || fx.expected;
  const diffs = [];
  const near = (a, b) => Math.abs(a - b) < 0.005;
  for (const [k, exp] of Object.entries(expected)) {
    const val = got[k];
    if (typeof exp === "boolean") {
      if (val !== exp) diffs.push(`${k}: expected ${exp}, got ${val}`);
    } else if (!near(Number(val), Number(exp))) {
      diffs.push(`${k}: expected ${exp}, got ${val}`);
    }
  }
  return { pass: diffs.length === 0, diffs, got };
}

// ═══════════════════════════════════════════════════════════════════
// SPRINT 3 · Cash variance arithmetic fixture
// ═══════════════════════════════════════════════════════════════════
// Definitions (single source of truth, mirrored in Sprint3.js):
//
//   expected_cash = opening_float + cash_tenders - cash_refunds - payouts
//   counted_cash  = sum of denomination grid (drawer contents)
//   variance      = counted_cash - expected_cash
//
// The opening float BELONGS in expected_cash. It is physically in the
// drawer at open and remains there at close, so a correct denomination
// count naturally includes it. Failing to include the float in the
// count produces a variance equal to -float (a classic UX trap that
// looks like a real bug).
//
// Worked case documented in this fixture:
//   opening_float =  2,500
//   cash_tenders  =  3,770         (from settled cash payments)
//   cash_refunds  =      0
//   payouts       =      0
//   expected_cash =  2,500 + 3,770 - 0 - 0 = 6,270
//   counted_cash  =  6,270         (drawer contains float + tenders)
//   variance      =  6,270 - 6,270 = 0
//
// Trap case (float NOT counted):
//   counted_cash  =  3,770
//   variance      =  3,770 - 6,270 = -2,500   ← equals opening float
//
export const VARIANCE_REGRESSION_FIXTURE = Object.freeze({
  name: "variance_with_float_v1",
  opening_float: 2500,
  cash_tenders:  3770,
  cash_refunds:  0,
  payouts:       0,
  denominations_ok: { 2000: 3, 500: 0, 100: 2, 50: 1, 20: 1, 5: 0 }, // 2000×3 + 100×2 + 50 + 20 = 6270
  denominations_trap: { 2000: 1, 500: 3, 100: 2, 50: 1, 20: 1 },     // 2000+1500+200+50+20 = 3770
  expected: {
    expected_cash: 6270,
    counted_ok:    6270,
    counted_trap:  3770,
    variance_ok:      0,
    variance_trap: -2500, // classic trap: NOT counting the float looks like the float has walked
  },
});

export function verifyVarianceFixture(fx = VARIANCE_REGRESSION_FIXTURE) {
  const sumDenoms = (dm) => Object.entries(dm).reduce((s, [d, q]) => s + Number(d) * Number(q), 0);
  const expected_cash = fx.opening_float + fx.cash_tenders - fx.cash_refunds - fx.payouts;
  const counted_ok    = sumDenoms(fx.denominations_ok);
  const counted_trap  = sumDenoms(fx.denominations_trap);
  const variance_ok   = counted_ok   - expected_cash;
  const variance_trap = counted_trap - expected_cash;
  const diffs = [];
  const near = (a, b) => Math.abs(a - b) < 0.005;
  if (!near(expected_cash, fx.expected.expected_cash))
    diffs.push(`expected_cash: expected ${fx.expected.expected_cash}, got ${expected_cash}`);
  if (!near(counted_ok, fx.expected.counted_ok))
    diffs.push(`counted_ok: expected ${fx.expected.counted_ok}, got ${counted_ok}`);
  if (!near(counted_trap, fx.expected.counted_trap))
    diffs.push(`counted_trap: expected ${fx.expected.counted_trap}, got ${counted_trap}`);
  if (!near(variance_ok, fx.expected.variance_ok))
    diffs.push(`variance_ok: expected ${fx.expected.variance_ok}, got ${variance_ok}`);
  if (!near(variance_trap, fx.expected.variance_trap))
    diffs.push(`variance_trap: expected ${fx.expected.variance_trap}, got ${variance_trap}`);
  return { pass: diffs.length === 0, diffs, got: { expected_cash, counted_ok, counted_trap, variance_ok, variance_trap } };
}

// Boot-time console assertion — intentionally non-blocking.
if (typeof window !== "undefined") {
  try {
    // The primary TAX_REGRESSION_FIXTURE was authored against
    // PER_COMPONENT rounding (its `expected` block uses ₹25/₹25/…).
    // The reducer default flipped to TOTAL_ONLY in Feb 2026 to match
    // the server, so the fixture is now run with an explicit
    // PER_COMPONENT override to preserve the original assertion.
    const r = verifyTaxFixture(TAX_REGRESSION_FIXTURE, {
      tax_rounding_mode: TAX_ROUNDING_MODES.PER_COMPONENT,
    });
    // eslint-disable-next-line no-console
    console[r.pass ? "log" : "error"](
      `[RestroPulse POS] Tax fixture "${TAX_REGRESSION_FIXTURE.name}" ${r.pass ? "PASSED" : "FAILED"}`,
      r.pass ? "" : r.diffs.join(" | "),
    );
    // Sprint 6 correction 1 · fractional GST + VAT fixture under both
    // rounding modes. Independent components rounding on their own,
    // round_off reconciling paise back to the payable amount.
    const rf1 = verifyTaxFixture(TAX_REGRESSION_FIXTURE_FRACTIONAL, {
      tax_rounding_mode: TAX_ROUNDING_MODES.PER_COMPONENT,
      expected: TAX_REGRESSION_FIXTURE_FRACTIONAL.expected_per_component,
    });
    // eslint-disable-next-line no-console
    console[rf1.pass ? "log" : "error"](
      `[RestroPulse POS] Tax fixture "${TAX_REGRESSION_FIXTURE_FRACTIONAL.name}" (per_component) ${rf1.pass ? "PASSED" : "FAILED"}`,
      rf1.pass ? "" : rf1.diffs.join(" | "),
    );
    const rf2 = verifyTaxFixture(TAX_REGRESSION_FIXTURE_FRACTIONAL, {
      tax_rounding_mode: TAX_ROUNDING_MODES.TOTAL_ONLY,
      expected: TAX_REGRESSION_FIXTURE_FRACTIONAL.expected_total_only,
    });
    // eslint-disable-next-line no-console
    console[rf2.pass ? "log" : "error"](
      `[RestroPulse POS] Tax fixture "${TAX_REGRESSION_FIXTURE_FRACTIONAL.name}" (total_only) ${rf2.pass ? "PASSED" : "FAILED"}`,
      rf2.pass ? "" : rf2.diffs.join(" | "),
    );
    const v = verifyVarianceFixture();
    // eslint-disable-next-line no-console
    console[v.pass ? "log" : "error"](
      `[RestroPulse POS] Variance fixture "${VARIANCE_REGRESSION_FIXTURE.name}" ${v.pass ? "PASSED" : "FAILED"}`,
      v.pass ? "" : v.diffs.join(" | "),
    );
  } catch { /* ignore */ }
}
